import type { Dictionary } from "@/lib/i18n/dictionaries/en";

const pt: Dictionary = {
  // ---- /citation-styles/alphabetical/<letter> ----
  styleDirectory: {
    notFoundTitle: "Diretório de estilos de citação não encontrado",
    metaTitle: "Estilos de citação que começam com {label}",
    metaDescription:
      "Navegue por {count} estilos Citation Style Language que começam com {label}. Abra qualquer estilo para ver exemplos reais de citação e um gerador gratuito.",
    ogSubtitle: "{count} estilos com exemplos e geradores dedicados.",
    ogKind: "Diretório A–Z",
    schemaName: "Estilos de citação que começam com {label}",
    schemaDescription:
      "Diretório alfabético de {count} estilos Citation Style Language que começam com {label}.",
    breadcrumbStyles: "Estilos de citação",
    heading: "Estilos de citação que começam com {label}",
    intro:
      "Navegue por todos os {count} estilos desta parte do catálogo CSL. Cada página inclui exemplos formatados do próprio estilo e um gerador de citações gratuito.",
    navLabel: "Estilos de citação de A a Z",
    listHeading: "Todos os estilos que começam com {label}",
  },
  nav: {
    tools: "Ferramentas",
    guides: "Guias",
    styles: "Todos os estilos",
    bibliography: "Minha bibliografia",
    search: "Buscar",
    glossary: "Glossário",
    home: "Início",
    menu: "Menu",
  },

  home: {
    title: "Gerador de referências gratuito",
    subtitle: "Atualizado para 2026",
    intro:
      "Cole um DOI, um link ou uma entrada BibTeX — ou pesquise um livro — e receba a referência formatada em ABNT, APA, MLA, Chicago, Vancouver e milhares de outros estilos. Totalmente gratuito, sem assinatura paga e sem cadastro.",
    browseAllStyles: "Ver os {count} estilos de citação →",
    featuresHeading: "Tudo o que você precisa para citar",
    featureStyles: "{count} estilos",
    featureStylesDetail: "ABNT, APA, MLA, Chicago e mais",
    featureBibliographies: "Várias bibliografias",
    featureBibliographiesDetail: "Organize por trabalho",
    featureBookSearch: "Busca de livros",
    featureBookSearchDetail: "Por título ou autor",
    featureExport: "Exporte para onde quiser",
    featureExportDetail: "BibTeX, Word, RIS, CSV",
    featureDarkMode: "Modo escuro",
    featureDarkModeDetail: "Confortável para os olhos",
    featureNoSignup: "Sem cadastro",
    featureNoSignupDetail: "Gratuito para sempre, sem assinatura paga",
    aboutHeading:
      "Um gerador de referências gratuito para estudantes e pesquisadores",
    aboutBody:
      "O CitationEasy oferece mais de 10.000 estilos de citação acadêmica. Seja ABNT para a universidade brasileira, APA para psicologia, MLA para letras, Chicago para história ou Vancouver para revistas médicas, a formatação vem da definição oficial do próprio estilo — não de uma aproximação.",
    howItWorksHeading: "Como funciona",
    howItWorksStep1:
      "Cole um DOI, um link, um ISBN, um identificador do PubMed ou do arXiv, ou uma entrada BibTeX; você também pode pesquisar um livro pelo título.",
    howItWorksStep2:
      "Escolha o estilo de citação. Digite para pesquisar entre os {count} disponíveis.",
    howItWorksStep3:
      "Copie a referência pronta ou salve-a em uma bibliografia e exporte para Word, BibTeX ou RIS.",
    privacyNote:
      "Sua bibliografia fica guardada no seu próprio navegador. Sem conta e sem enviar nada para servidor nenhum.",
  },

  hubs: {
    toolsTitle: "Ferramentas gratuitas de escrita e citação",
    toolsIntro:
      "{count} ferramentas que funcionam no navegador para escrever e citar: geradores para cada tipo de fonte, contadores de palavras e caracteres, análise de legibilidade, formatadores e conversores. Todas gratuitas e instantâneas.",
    stylesTitle: "Explore todos os estilos de citação",
    stylesIntro:
      "Os {count} estilos do projeto Citation Style Language, com busca e filtros por país, assunto e área acadêmica.",
    guidesTitle: "Guias de citação e escrita acadêmica",
    guidesIntro:
      "Guias detalhados sobre como citar cada tipo de fonte, como avaliar o que você encontra e como escrever melhor.",
    howToCiteTitle: "Como citar qualquer fonte",
    howToCiteIntro:
      "Páginas passo a passo que combinam cada tipo de fonte comum com os principais estilos de citação, cada uma com um exemplo real resolvido.",
    faqTitle: "Perguntas frequentes sobre citação",
    faqIntro:
      "Respostas curtas e precisas para as dúvidas que os estudantes realmente têm na hora de citar.",
    glossaryTitle: "Glossário de citação",
    glossaryIntro:
      "Definições claras dos termos que aparecem nos manuais de estilo e nos enunciados dos trabalhos.",
    openHub: "Abrir",
  },

  styleLanding: {
    metaTitle: "Gerador de citações {style} gratuito",
    metaDescription:
      "Gere citações em {style} de graça. Cole um DOI, link, ISBN ou BibTeX e receba a referência e a citação no texto no formato {style} correto.",
    heading: "Gerador de citações {style}",
    intro:
      "Crie citações {style} precisas para livros, artigos de periódico e sites com um clique. Gratuito e sem cadastro.",
    aboutHeading: "Sobre {style}",
    whenToUseHeading: "Quando usar {style}",
    factsHeading: "{style} em resumo",
    factEdition: "Edição",
    factSystem: "Sistema de citação",
    factInText: "Formato no texto",
    factReferenceList: "Nome da lista",
    factFields: "Comum em",
    systemAuthorDate: "Autor–data",
    systemNumbered: "Numérico",
    systemFootnotes: "Notas de rodapé",
    howToHeading: "Como usar este gerador {style}",
    howToStep1:
      "Cole um link, DOI, ISBN ou entrada BibTeX na caixa acima, ou pesquise um livro pelo título.",
    howToStep2: "{style} já está selecionado nesta página, então é só gerar.",
    howToStep3:
      "Copie o resultado para o seu documento, ou salve e exporte a lista inteira depois.",
    relatedHeading: "Mais recursos sobre {style}",
    englishOnlyNote:
      "Os guias detalhados de {style} e os exemplos por tipo de fonte estão disponíveis em inglês.",
  },

  // The per-style pages at `/<locale>/citation-style/<slug>` — one per catalog style.
  //
  // The formatted examples the page is built around (the style's own output for a
  // fixed journal article, book and website) and the style's own name stay in
  // `src/data/citation-styles.ts` and `src/data/style-examples-generated.json`. They
  // are language-neutral: a citation rendered by the style's own CSL definition looks
  // the same whatever language the reader speaks, and "translating" one would make the
  // page wrong.
  //
  // The rest of what `src/data/**` supplies to these pages is NOT like that — see the
  // `generic*`, `discipline*` and `internationalLabel` keys below.
  citationStylePage: {
    notFoundTitle: "Estilo de citação não encontrado",
    metaTitle: "Gerador de citações {style} - Formato {style} gratuito",
    metaFormatAndFields: "{style} é um estilo {format} usado em {fields}.",
    metaFormatOnly: "{style} é um estilo {format}.",
    metaFieldsOnly: "{style} é usado em {fields}.",
    metaPromiseNote:
      "Veja uma nota de rodapé real em {style} e gere a sua de graça.",
    metaPromiseExamples:
      "Veja exemplos reais de citação em {style} para um artigo de periódico, um livro e um site, e gere a sua de graça.",
    ogKindFormat: "Estilo {format}",
    ogKindDefault: "Estilo de citação",
    // Joins the style's discipline list: "psychology, education and nursing".
    disciplineConjunction: "e",
    // ---- Data-derived facts that are nonetheless mail-merged prose ----
    //
    // Every entry in `src/data/citation-styles-generated.ts` carries exactly the
    // two sentences below with their own name spliced in, exactly one region value
    // ("International" — that is `internationalLabel` above, reused rather than
    // duplicated), and discipline tokens drawn from a closed 26-word vocabulary. None
    // of that is a citation, so none of it is language-neutral the way the formatted
    // examples are. Left in `src/data/**` it renders English facts inside translated
    // chrome, which is precisely the hybrid page this tree exists to avoid.
    //
    // The page substitutes the two templates ONLY when a style's stored text is
    // byte-identical to the English template with its own name interpolated, so the
    // ~100 hand-curated styles keep their bespoke English descriptions instead of
    // being flattened into a generic sentence. `{name}` is the style's full name.
    genericDescription:
      "Estilo de citação para {name}. Do repositório de Citation Style Language.",
    genericWhenUsed:
      "Ao enviar trabalhos para publicações que usam o estilo {name}, ou ao citar nelas.",

    // The 26 discipline tokens `style-examples-generated.json` uses. Each English
    // value is the exact string the page printed before these keys existed — the raw
    // token with hyphens turned into spaces — so `generic-base` reads "generic base"
    // and `political_science` keeps its underscore. An unknown token (a new one from
    // upstream CSL) falls back to that same prettified form rather than to a key name.
    disciplineAnthropology: "antropologia",
    disciplineAstronomy: "astronomia",
    disciplineBiology: "biologia",
    disciplineBotany: "botânica",
    disciplineChemistry: "química",
    disciplineCommunications: "comunicação",
    disciplineEngineering: "engenharia",
    disciplineGenericBase: "geral",
    disciplineGeography: "geografia",
    disciplineGeology: "geologia",
    disciplineHistory: "história",
    disciplineHumanities: "humanidades",
    disciplineLaw: "direito",
    disciplineLinguistics: "linguística",
    disciplineLiterature: "literatura",
    disciplineMath: "matemática",
    disciplineMedicine: "medicina",
    disciplinePhilosophy: "filosofia",
    disciplinePhysics: "física",
    disciplinePoliticalScience: "ciência política",
    disciplinePsychology: "psicologia",
    disciplineScience: "ciência",
    disciplineSocialScience: "ciências sociais",
    disciplineSociology: "sociologia",
    disciplineTheology: "teologia",
    disciplineZoology: "zoologia",

    // Plain-language descriptions of the citation-format classes the CSL definitions
    // declare. `{format}` in the fallback is the raw class name from the data.
    formatClassAuthorDate:
      "um estilo autor-data: as citações no texto trazem o autor e o ano, e a lista de referências é organizada em ordem alfabética por autor",
    formatClassNumeric:
      "um estilo numérico: as fontes são numeradas na ordem em que são citadas pela primeira vez, e as citações no texto usam esses números",
    formatClassNote:
      "um estilo baseado em notas: as citações aparecem em notas de rodapé ou notas finais, em vez de parênteses no texto",
    formatClassLabel:
      "um estilo de rótulo: as citações no texto usam um rótulo compacto formado pelo nome do autor e o ano",
    formatClassAuthor:
      "um estilo somente-autor: as citações no texto trazem o nome do autor, com os detalhes completos na lista de referências",
    formatClassOther: "um estilo {format}",
    breadcrumbStyles: "Estilos de citação",
    internationalLabel: "Internacional",
    heading: "Gerador de citações {style}",
    intro:
      "Crie citações {style} precisas para livros, artigos e sites com um clique.",
    examplesHeading: "Exemplos de citação em {style}",
    examplesIntro: "As mesmas três fontes formatadas em {style}:",
    examplesIntroWithFormat:
      "As mesmas três fontes formatadas em {style} — {format}:",
    examplesNoteOnlyIntro:
      "{style} é um estilo baseado em notas: não há lista de referências, e cada citação é uma nota de rodapé. Veja nosso artigo de periódico de exemplo citado em {style}:",
    labelJournalArticle: "Artigo de periódico",
    labelBook: "Livro",
    labelWebsite: "Site",
    labelFootnote: "Nota de rodapé (artigo de periódico)",
    labelInText: "Citação no texto (do artigo de periódico)",
    aboutHeading: "Sobre {style}",
    whenToUseLabel: "Quando usar {style}:",
    commonInLabel: "Comum em:",
    subjectAreasLabel: "Áreas de estudo:",
    formattingRulesLabel: "Regras de formatação:",
    parentStylePrefix: "{style} segue a formatação de",
    parentStyleSuffix: ", o estilo original em que se baseia.",
    howToHeading: "Como usar este gerador {style}",
    howToBody:
      'Cole um DOI, a URL de uma notícia ou página, ou um BibTeX bruto na caixa acima. Você também pode pesquisar um livro pelo título ou autor e citá-lo pelo ISBN. Escolha "{style}" no menu de estilos (ele já vem selecionado nesta página) e clique em "Gerar referência". Copie o resultado para a sua lista de referências ou bibliografia. Você pode editar a entrada gerada (por exemplo, corrigir um título ou adicionar uma data) antes de copiar.',
    faqHeading: "Perguntas frequentes sobre {style}",
    faqWhatIsQ: "O que é {style}?",
    faqWhenQ: "Quando devo usar {style}?",
    faqInTextQ: "Como é uma citação no texto em {style}?",
    faqInTextA:
      "{style} é {format}. Citar nosso artigo de periódico de exemplo no texto fica assim: {example}",
    faqReferenceQ: "Como é uma entrada da lista de referências em {style}?",
    faqReferenceA:
      "Um artigo de periódico formatado em {style} fica assim: {example}",
    faqWhereQ: "Onde {style} é normalmente usado?",
    faqWhereA: "{style} é usado {region}. {whenUsed}",
    faqRegionDefault: "internacionalmente",
    faqGenerateQ: "Como posso gerar uma citação em {style}?",
    faqGenerateA:
      "Use o gerador gratuito desta página: cole um DOI, uma URL (por exemplo, de uma notícia) ou um BibTeX, ou pesquise um livro pelo título ou autor. Selecione {style} como o estilo de citação e clique em Gerar para obter uma referência formatada que você pode copiar para o seu trabalho ou bibliografia.",
    footerSupports:
      "aceita muitos estilos de citação do mundo todo. Nosso gerador usa estilos Citation Style Language (CSL) do",
    footerRepoLink: "repositório oficial do CSL",
  },

  // The source × style pages at `/<locale>/how-to-cite/<slug>` — 1,290 combinations.
  //
  // Same division as above: the citation templates, the worked examples generated from
  // each style's own CSL definition, the in-text samples and the hand-checked
  // formatting rules stay in the page module and in
  // `src/data/how-to-cite-examples-generated.json`. They are the citations themselves,
  // not prose about them. Source names, style names and editions come from
  // `src/data/how-to-cite.ts` and arrive here as placeholder values.
  crossLinks: {
    generateHeading: "Gere esta citação",
    generateBody:
      "Cole um URL, DOI, ISBN ou os dados da fonte e cite {source} em {style} na hora.",
    generateCta: "Abrir o {tool} →",
    guideHeading: "Aprofunde-se",
    guideCta: "Leia o guia completo: {guide}",
    howToCiteHeading: "Como citar {source} em cada estilo",
    howToCiteBody:
      "Regras de formatação, modelos e exemplos resolvidos para os estilos mais exigidos.",
    howToCiteMore: "Todos os tipos de fonte e estilos →",
    citeNowHeading: "Cite {source} em {style} agora",
    citeNowHowTo: "Regras de formatação e exemplos resolvidos",
    citeNowTool: "Gere com o {tool}",
    citeNowHub: "Gerador de citações {style}",
    hubSourcesHeading: "Como citar as fontes mais comuns em {style}",
    hubGuidesHeading: "Guias de {style}",
    hubCompareHeading: "Compare {style} com outros estilos",
    moreInCollection: "Mais em {collection}",
    seeAllInCollection: "Ver tudo em {collection}",
  },

  howToCitePage: {
    notFoundTitle: "Não encontrado",
    title: "Como citar {source} em {style} ({edition})",
    metaTitle: "Como citar {source} em {style} [{edition}, atualizado em 2026]",
    metaDescription:
      "Aprenda a citar {description} no formato {style} ({edition}). Exemplos gratuitos, modelos e guias de citação no texto.",
    keywordCite: "citar {source} {style}",
    keywordCitation: "citação {style} {source}",
    keywordHowTo: "como citar {source}",
    keywordExample: "exemplo de citação {style}",
    keywordReference: "referência {source} {style}",
    // What each style calls the list at the back of the paper, in the two casings the
    // prose needs. MLA says "Works Cited", Chicago and the legal styles "Bibliography".
    refListWorksCited: "Lista de obras citadas",
    refListWorksCitedPage: "lista de obras citadas",
    refListBibliography: "Bibliografia",
    refListBibliographyLower: "bibliografia",
    refListReferenceList: "Referências",
    refListReferenceListLower: "lista de referências",
    backLink: "← Todos os guias de citação",
    lead: "Aprenda a citar {description} no formato {style} ({edition}). Este guia traz o formato da lista de referências, exemplos de citação no texto e as principais regras de formatação.",
    referenceFormatHeading: "Formato de {list} em {style}",
    referenceFormatIntro:
      "Este é o formato padrão de {style} para citar {source} como entrada de {list}:",
    noTemplateNote: "Siga o modelo padrão de {style} para este tipo de fonte.",
    workedHeading: "Exemplo de citação em {style} para {source}",
    workedIntro:
      "Uma referência completa em {style} para {source}, formatada pela definição oficial do estilo CSL de {style} — o mesmo mecanismo que move o gerador deste site:",
    workedNoteOnlyIntro:
      "{style} não prevê uma entrada de {list} para {source} — a fonte é citada apenas em uma nota. Veja essa nota, formatada pela definição oficial do estilo CSL de {style}:",
    workedOscolaNote:
      "A saída OSCOLA gerada neste site segue o estilo CSL da 4ª edição, porque ainda não há um estilo CSL da 5ª edição publicado no repositório do Citation Style Language. Ajuste-a manualmente: troque cada ibid pelo nome abreviado e (n X), use os identificadores de tribunal da 5ª edição, coloque a forma abreviada de uma lei entre aspas simples e prefira um link permanente ou um DOI à data de acesso",
    workedCheckedIntro:
      "Uma referência completa em {style} para {source}, escrita à mão e conferida com as orientações oficiais de {style} para este tipo de fonte:",
    workedCheckedNoteOnlyIntro:
      "{style} não prevê uma entrada de {list} para {source} — a fonte é citada apenas em uma nota. Veja essa nota, escrita à mão e conferida com as orientações oficiais de {style}:",
    workedInTextLabel: "Citação correspondente no texto:",
    inTextHeading: "Citação no texto em {style}",
    inTextIntro:
      "Use um destes formatos ao referenciar esta fonte no seu trabalho:",
    inTextParenthetical: "Entre parênteses",
    inTextNarrative: "Narrativa",
    rulesHeading: "Principais regras de formatação de {style}",
    stepsHeading: "Guia passo a passo",
    step1Label: "Reúna as informações da fonte:",
    step1Body:
      "Reúna o nome do autor, o título, a data de publicação e a URL ou o DOI — os dados básicos de uma citação de {source}.",
    step2Label: "Formate a referência:",
    step2Body:
      "Organize os elementos seguindo o modelo de {style} ({edition}) mostrado acima.",
    step3Label: "Crie a citação no texto:",
    step3Body:
      "Adicione uma citação entre parênteses ou narrativa no seu trabalho, no ponto em que você referencia esta fonte.",
    step4Label: "Adicione à parte {list}:",
    step4Body:
      "Inclua a citação completa e formatada no final do seu trabalho.",
    step5Label: "Revise a formatação:",
    step5Body:
      "Confira se o itálico, a pontuação, as maiúsculas e o recuo deslocado seguem as exigências de {style}.",
    ctaHeading: "Gere esta citação automaticamente",
    ctaBody:
      "Pule a formatação manual. Cole um DOI, uma URL ou um ISBN no CitationEasy e receba uma citação em {style} perfeitamente formatada em segundos.",
    ctaButton: "Citar em {style} agora →",
    otherStylesHeading: "Cite {source} em outros estilos",
    comboLink: "{source} em {style}",
    otherSourcesHeading: "Cite outras fontes em {style}",
    resourcesHeading: "Mais recursos",
    resourceGenerator: "Gerador de citações gratuito ({count} estilos)",
    resourceTools: "Ferramentas de escrita gratuitas",
    resourceCompare: "Comparar estilos de citação",
    resourceGuides: "Todos os guias de citação",
    resourceCheatSheets: "Guias rápidos (APA, MLA, Chicago)",
    resourceGlossary:
      "Glossário de citação (et al., ibid., DOI, recuo deslocado)",
    // HowTo structured data. Deliberately worded differently from the visible steps
    // above: the schema describes the task generically, the page walks the reader
    // through this particular source and style.
    schemaStep1Name: "Reunir as informações da fonte",
    schemaStep1Text:
      "Reúna todas as informações necessárias para uma citação de {source}, incluindo autor, título, data e URL ou DOI.",
    schemaStep2Name: "Formatar em {style}",
    schemaStep2Text:
      "Siga as regras de formatação de {style} ({edition}) para organizar os elementos da citação na ordem correta.",
    schemaStep3Name: "Criar a citação no texto",
    schemaStep3Text:
      "Adicione a citação correspondente no texto do seu trabalho, usando o formato {style}.",
    schemaStep4Name: "Adicionar à lista de referências",
    schemaStep4Text:
      "Inclua a citação completa em {list}, no final do trabalho.",
    faqHowQ: "Como citar {source} em {style}?",
    faqHowA:
      "Para citar {source} em {style} ({edition}), inclua o autor, o título, a data e a localização da fonte. Use nosso gerador de citações gratuito para a formatação automática.",
    faqFormatQ: "Qual é o formato {style} para citar {source}?",
    faqFormatA: "O formato {style} para {source} é: {example}",
    faqFormatFallback:
      "O formato {style} exige elementos específicos, como autor, título, data e localização da fonte, organizados em uma ordem específica.",
    faqInTextQ: "Como é a citação no texto para {source} em {style}?",
    faqInTextA: "Em {style}, a citação no texto do exemplo acima é {example}",
  },

  // The 43 source types the how-to-cite pages are built from (`SOURCE_TYPES` in
  // `src/data/how-to-cite.ts`), in the five forms those pages print. Each form is a whole
  // phrase, never an article and a noun joined in code: an article agrees with its noun
  // in gender, number and case, elides in French and Italian, and has no counterpart
  // at all in Polish, Russian or Japanese.
  //
  // - `…Name` ("Book") is the type on its own, as a label: `howToCitePage.comboLink`.
  //   English also lower-cases it into the `keyword*` templates.
  // - `…Title` ("a Book") is the type as the object of "cite" in a title or heading:
  //   `title`, `metaTitle`, `workedHeading` and `otherStylesHeading`, plus
  //   `crossLinks.generateBody`, `howToCiteHeading` and `citeNowHeading`. Through
  //   `title` it is also the HowTo name, the OG card and every link to the page.
  // - `…Phrase` ("a book") is the same inside a sentence: `referenceFormatIntro`,
  //   `workedIntro`, `workedNoteOnlyIntro` and the five `faq*` questions and answers.
  // - `…Noun` ("book") is the bare noun inside a sentence, where English puts "your" in
  //   front of it: `step1Body` and `schemaStep1Text`. A translation also uses it in the
  //   `keyword*` templates, which read as search queries.
  // - `…Description` is what the type covers: `{description}` in `lead` and
  //   `metaDescription`.
  //
  // A language with case picks one case per form and words every template above around
  // it — German writes Title and Phrase in the accusative, Polish and Russian write Name
  // and Noun in the nominative. Changing a form's case means re-reading every template
  // that receives it. A key missing here falls back to the English data
  // (`howToCiteSourceForms`), so a new source type renders in English, not as a key.
  // Portuguese: Title/Phrase carry their article ("a Bíblia"), never after de/em/a; Name/Noun bare.
  howToCiteSources: {
    websiteName: "Site",
    websiteTitle: "um site",
    websitePhrase: "um site",
    websiteNoun: "site",
    websiteDescription:
      "páginas da web, artigos online e outros conteúdos da internet",

    bookName: "Livro",
    bookTitle: "um livro",
    bookPhrase: "um livro",
    bookNoun: "livro",
    bookDescription: "livros impressos e eletrônicos",

    journalArticleName: "Artigo de periódico",
    journalArticleTitle: "um artigo de periódico",
    journalArticlePhrase: "um artigo de periódico",
    journalArticleNoun: "artigo de periódico",
    journalArticleDescription:
      "artigos de periódicos acadêmicos e revisados por pares",

    youtubeVideoName: "Vídeo do YouTube",
    youtubeVideoTitle: "um vídeo do YouTube",
    youtubeVideoPhrase: "um vídeo do YouTube",
    youtubeVideoNoun: "vídeo do YouTube",
    youtubeVideoDescription: "vídeos do YouTube e outros vídeos online",

    podcastName: "Podcast",
    podcastTitle: "um podcast",
    podcastPhrase: "um podcast",
    podcastNoun: "podcast",
    podcastDescription: "episódios e séries de podcast",

    newspaperArticleName: "Artigo de jornal",
    newspaperArticleTitle: "um artigo de jornal",
    newspaperArticlePhrase: "um artigo de jornal",
    newspaperArticleNoun: "artigo de jornal",
    newspaperArticleDescription: "artigos de jornais impressos e online",

    magazineArticleName: "Artigo de revista",
    magazineArticleTitle: "um artigo de revista",
    magazineArticlePhrase: "um artigo de revista",
    magazineArticleNoun: "artigo de revista",
    magazineArticleDescription: "artigos de revistas impressas e online",

    blogPostName: "Post de blog",
    blogPostTitle: "um post de blog",
    blogPostPhrase: "um post de blog",
    blogPostNoun: "post de blog",
    blogPostDescription: "posts de blog e artigos de opinião online",

    wikipediaName: "Wikipédia",
    wikipediaTitle: "a Wikipédia",
    wikipediaPhrase: "a Wikipédia",
    wikipediaNoun: "artigo da Wikipédia",
    wikipediaDescription: "artigos da Wikipédia e verbetes de wikis",

    pdfName: "PDF",
    pdfTitle: "um PDF",
    pdfPhrase: "um PDF",
    pdfNoun: "PDF",
    pdfDescription: "documentos e relatórios em PDF",

    interviewName: "Entrevista",
    interviewTitle: "uma entrevista",
    interviewPhrase: "uma entrevista",
    interviewNoun: "entrevista",
    interviewDescription:
      "entrevistas pessoais, publicadas e transmitidas por rádio ou TV",

    socialMediaName: "Post de rede social",
    socialMediaTitle: "um post de rede social",
    socialMediaPhrase: "um post de rede social",
    socialMediaNoun: "post de rede social",
    socialMediaDescription:
      "tweets, posts do Instagram e do Facebook e vídeos do TikTok",

    governmentDocumentName: "Documento governamental",
    governmentDocumentTitle: "um documento governamental",
    governmentDocumentPhrase: "um documento governamental",
    governmentDocumentNoun: "documento governamental",
    governmentDocumentDescription:
      "relatórios governamentais, legislação e documentos oficiais",

    dissertationName: "Tese ou dissertação",
    dissertationTitle: "uma tese ou dissertação",
    dissertationPhrase: "uma tese ou dissertação",
    dissertationNoun: "tese ou dissertação",
    dissertationDescription: "teses de doutorado e dissertações de mestrado",

    conferencePaperName: "Artigo de congresso",
    conferencePaperTitle: "um artigo de congresso",
    conferencePaperPhrase: "um artigo de congresso",
    conferencePaperNoun: "artigo de congresso",
    conferencePaperDescription: "anais de congressos e apresentações",

    lectureNotesName: "Aula ou PowerPoint",
    lectureNotesTitle: "uma aula ou um PowerPoint",
    lectureNotesPhrase: "uma aula ou um PowerPoint",
    lectureNotesNoun: "aula ou PowerPoint",
    lectureNotesDescription: "aulas, slides e materiais de curso",

    webinarName: "Webinar",
    webinarTitle: "um webinar",
    webinarPhrase: "um webinar",
    webinarNoun: "webinar",
    webinarDescription: "webinars e seminários online ao vivo ou gravados",

    onlineCourseName: "Curso online",
    onlineCourseTitle: "um curso online",
    onlineCoursePhrase: "um curso online",
    onlineCourseNoun: "curso online",
    onlineCourseDescription:
      "cursos online e MOOCs (Coursera, edX, Udemy, FutureLearn)",

    presentationSlidesName: "Apresentação de PowerPoint",
    presentationSlidesTitle: "uma apresentação de PowerPoint",
    presentationSlidesPhrase: "uma apresentação de PowerPoint",
    presentationSlidesNoun: "apresentação de PowerPoint",
    presentationSlidesDescription:
      "slides de PowerPoint e de outras apresentações",

    imageName: "Imagem ou fotografia",
    imageTitle: "uma imagem ou fotografia",
    imagePhrase: "uma imagem ou fotografia",
    imageNoun: "imagem ou fotografia",
    imageDescription: "fotografias, obras de arte, gráficos e imagens online",

    movieName: "Filme",
    movieTitle: "um filme",
    moviePhrase: "um filme",
    movieNoun: "filme",
    movieDescription: "um filme",

    documentaryName: "Documentário",
    documentaryTitle: "um documentário",
    documentaryPhrase: "um documentário",
    documentaryNoun: "documentário",
    documentaryDescription: "documentários e filmes de não ficção",

    tvShowName: "Programa de TV",
    tvShowTitle: "um programa de TV",
    tvShowPhrase: "um programa de TV",
    tvShowNoun: "programa de TV",
    tvShowDescription: "um programa ou uma série de TV",

    songName: "Canção ou música",
    songTitle: "uma canção ou música",
    songPhrase: "uma canção ou música",
    songNoun: "música",
    songDescription: "uma música ou uma gravação musical",

    poemName: "Poema",
    poemTitle: "um poema",
    poemPhrase: "um poema",
    poemNoun: "poema",
    poemDescription: "poemas, versos e poesia de antologias ou coletâneas",

    courtCaseName: "Caso judicial",
    courtCaseTitle: "um caso judicial",
    courtCasePhrase: "um caso judicial",
    courtCaseNoun: "caso judicial",
    courtCaseDescription: "um caso ou uma decisão judicial",

    patentName: "Patente",
    patentTitle: "uma patente",
    patentPhrase: "uma patente",
    patentNoun: "patente",
    patentDescription:
      "uma patente ou outro registro de propriedade intelectual",

    encyclopediaName: "Verbete de enciclopédia",
    encyclopediaTitle: "um verbete de enciclopédia",
    encyclopediaPhrase: "um verbete de enciclopédia",
    encyclopediaNoun: "verbete de enciclopédia",
    encyclopediaDescription: "um artigo ou verbete de enciclopédia",

    dictionaryName: "Verbete de dicionário",
    dictionaryTitle: "um verbete de dicionário",
    dictionaryPhrase: "um verbete de dicionário",
    dictionaryNoun: "verbete de dicionário",
    dictionaryDescription: "uma definição ou um verbete de dicionário",

    datasetName: "Conjunto de dados",
    datasetTitle: "um conjunto de dados",
    datasetPhrase: "um conjunto de dados",
    datasetNoun: "conjunto de dados",
    datasetDescription: "um conjunto de dados ou um banco de dados",

    softwareName: "Software ou aplicativo",
    softwareTitle: "um software ou aplicativo",
    softwarePhrase: "um software ou aplicativo",
    softwareNoun: "software ou aplicativo",
    softwareDescription:
      "um software, um aplicativo ou um programa de computador",

    videoGameName: "Videogame",
    videoGameTitle: "um videogame",
    videoGamePhrase: "um videogame",
    videoGameNoun: "videogame",
    videoGameDescription: "videogames e softwares de jogos interativos",

    pressReleaseName: "Comunicado de imprensa",
    pressReleaseTitle: "um comunicado de imprensa",
    pressReleasePhrase: "um comunicado de imprensa",
    pressReleaseNoun: "comunicado de imprensa",
    pressReleaseDescription: "um comunicado de imprensa ou uma nota à imprensa",

    reportName: "Relatório",
    reportTitle: "um relatório",
    reportPhrase: "um relatório",
    reportNoun: "relatório",
    reportDescription: "um relatório ou um documento técnico",

    preprintName: "Preprint",
    preprintTitle: "um preprint",
    preprintPhrase: "um preprint",
    preprintNoun: "preprint",
    preprintDescription:
      "artigos do arXiv, do bioRxiv e de outros servidores de preprints",

    bookChapterName: "Capítulo de livro",
    bookChapterTitle: "um capítulo de livro",
    bookChapterPhrase: "um capítulo de livro",
    bookChapterNoun: "capítulo de livro",
    bookChapterDescription: "capítulos e ensaios de obras coletivas",

    chatgptName: "ChatGPT",
    chatgptTitle: "o ChatGPT",
    chatgptPhrase: "o ChatGPT",
    chatgptNoun: "ChatGPT",
    chatgptDescription: "o ChatGPT e outras ferramentas de IA generativa",

    bibleName: "Bíblia",
    bibleTitle: "a Bíblia",
    biblePhrase: "a Bíblia",
    bibleNoun: "texto bíblico",
    bibleDescription: "a Bíblia, as escrituras e outros textos sagrados",

    tedTalkName: "Palestra TED",
    tedTalkTitle: "uma palestra TED",
    tedTalkPhrase: "uma palestra TED",
    tedTalkNoun: "palestra TED",
    tedTalkDescription:
      "palestras TED e outras apresentações gravadas em conferências",

    emailName: "E-mail",
    emailTitle: "um e-mail",
    emailPhrase: "um e-mail",
    emailNoun: "e-mail",
    emailDescription: "e-mails, cartas e comunicações pessoais",

    redditName: "Post do Reddit ou de fórum",
    redditTitle: "um post do Reddit ou de fórum",
    redditPhrase: "um post do Reddit",
    redditNoun: "post do Reddit",
    redditDescription:
      "tópicos do Reddit, AMAs e posts em fóruns ou painéis de discussão online",

    tweetName: "Post do X (Twitter)",
    tweetTitle: "um post do X (Twitter)",
    tweetPhrase: "um post do X",
    tweetNoun: "post do X",
    tweetDescription: "tweets e posts no X (antigo Twitter)",

    mapName: "Mapa",
    mapTitle: "um mapa",
    mapPhrase: "um mapa",
    mapNoun: "mapa",
    mapDescription:
      "mapas impressos, atlas e mapas dinâmicos como o Google Maps",

    claudeName: "Claude",
    claudeTitle: "o Claude",
    claudePhrase: "o Claude",
    claudeNoun: "Claude",
    claudeDescription:
      "conversas com o Claude e outros conteúdos de IA da Anthropic",

    geminiName: "Gemini",
    geminiTitle: "o Gemini",
    geminiPhrase: "o Gemini",
    geminiNoun: "Gemini",
    geminiDescription:
      "conversas com o Google Gemini e conteúdos gerados por IA",

    copilotName: "Microsoft Copilot",
    copilotTitle: "o Microsoft Copilot",
    copilotPhrase: "o Microsoft Copilot",
    copilotNoun: "Microsoft Copilot",
    copilotDescription: "conversas e respostas de IA do Microsoft Copilot",

    perplexityName: "Perplexity",
    perplexityTitle: "o Perplexity",
    perplexityPhrase: "o Perplexity",
    perplexityNoun: "Perplexity",
    perplexityDescription:
      "respostas e conversas do Perplexity AI e as fontes que elas citam",

    aiImageName: "Imagem gerada por IA",
    aiImageTitle: "uma imagem gerada por IA",
    aiImagePhrase: "uma imagem gerada por IA",
    aiImageNoun: "imagem gerada por IA",
    aiImageDescription:
      "imagens criadas com DALL·E, Midjourney, Gemini e outras ferramentas de IA",

    tiktokName: "Vídeo do TikTok",
    tiktokTitle: "um vídeo do TikTok",
    tiktokPhrase: "um vídeo do TikTok",
    tiktokNoun: "vídeo do TikTok",
    tiktokDescription: "vídeos e publicações de criadores no TikTok",

    instagramName: "Post do Instagram",
    instagramTitle: "um post do Instagram",
    instagramPhrase: "um post do Instagram",
    instagramNoun: "post do Instagram",
    instagramDescription: "posts, reels e stories do Instagram",

    statuteName: "Lei ou ato normativo",
    statuteTitle: "uma lei ou ato normativo",
    statutePhrase: "uma lei ou ato normativo",
    statuteNoun: "lei ou ato normativo",
    statuteDescription: "atos do Poder Legislativo, leis e códigos",

    isoStandardName: "Norma ISO",
    isoStandardTitle: "uma norma ISO",
    isoStandardPhrase: "uma norma ISO",
    isoStandardNoun: "norma ISO",
    isoStandardDescription:
      "normas ISO, IEC e outras normas técnicas publicadas",

    quranName: "Alcorão",
    quranTitle: "o Alcorão",
    quranPhrase: "o Alcorão",
    quranNoun: "Alcorão",
    quranDescription: "o Alcorão, em árabe ou em tradução",

    ebookNoPageNumbersName: "E-book sem numeração de páginas",
    ebookNoPageNumbersTitle: "um e-book sem numeração de páginas",
    ebookNoPageNumbersPhrase: "um e-book sem numeração de páginas",
    ebookNoPageNumbersNoun: "e-book sem numeração de páginas",
    ebookNoPageNumbersDescription:
      "e-books do Kindle, em EPUB e online que não têm numeração fixa de páginas",

    speechName: "Discurso",
    speechTitle: "um discurso",
    speechPhrase: "um discurso",
    speechNoun: "discurso",
    speechDescription:
      "discursos, pronunciamentos e sermões, ao vivo ou gravados",

    letterName: "Carta",
    letterTitle: "uma carta",
    letterPhrase: "uma carta",
    letterNoun: "carta",
    letterDescription: "cartas publicadas, cartas de arquivo e cartas abertas",

    secondarySourceName: "Fonte secundária",
    secondarySourceTitle: "uma fonte secundária",
    secondarySourcePhrase: "uma fonte secundária",
    secondarySourceNoun: "fonte secundária",
    secondarySourceDescription:
      "uma fonte que você encontrou citada em outra obra",
  },

  // ---------------------------------------------------------------------------
  // NOT YET TRANSLATED. These five sections were copied verbatim from `en.ts` so
  // this file still satisfies `Dictionary`; the page types they cover
  // (`taxonomy`, `compare`, `convert`) are deliberately absent from
  // TRANSLATED_PAGE_TYPES, so nothing publishes them in this language yet. The
  // translation pass replaces the strings and only then adds the types.
  // ---------------------------------------------------------------------------
  // The three browse-by-facet pages under `/<locale>/citation-styles/…` — 32 academic
  // fields, 68 countries and 39 subject categories, 139 pages in one section because
  // they share a shell, a breadcrumb trail and a styles browser.
  //
  // The style NAMES, slugs and the CSL catalog itself stay in `src/data/**`: those are
  // subject matter. What the facets themselves are called is not — "Political Science",
  // "United Kingdom" and "Film & Media" are ordinary nouns that every reader expects in
  // their own language, and there are only 139 of them in total (see the `academicFields`,
  // `regions` and `styleCategories` sections below).
  taxonomyPage: {
    // ---- /citation-styles/field/<slug> ----
    fieldNotFoundTitle: "Não encontrado",
    fieldMetaTitle:
      "Estilos de citação para {field} - Os melhores estilos de referência [2026]",
    fieldMetaDescription:
      "Descubra os estilos de citação mais usados em {field}. Encontre o formato de referência certo para o seu trabalho, tese ou artigo de periódico de {fieldLower}.",
    fieldOgTitle: "Estilos de citação para {field}",
    fieldOgSubtitle: "Comumente exigidos: {styles}.",
    fieldOgKind: "Por área acadêmica",
    fieldKeywordStyle: "estilo de citação de {field}",
    fieldKeywordReferencing: "referências de {field}",
    fieldKeywordBest: "melhor estilo de citação para {fieldLower}",
    fieldKeywordBibliography: "formato de bibliografia de {fieldLower}",
    fieldKeywordDiscipline: "estilos de citação por área acadêmica",
    fieldBackLink: "← Todos os estilos de citação",
    fieldHeading: "Estilos de citação para {field}",
    fieldRecommendedHeading: "Estilos de citação recomendados",
    fieldRecommendedIntro:
      "Os estilos de citação a seguir são comumente usados em pesquisas, periódicos e trabalhos de {field}. Selecione um estilo para saber mais e começar a gerar citações.",
    fieldStyleFallback:
      "Um estilo de citação usado em {field} e áreas relacionadas.",
    fieldRelatedHeading: "Áreas relacionadas",
    fieldRelatedIntro:
      "Explore os estilos de citação usados em áreas acadêmicas relacionadas:",
    fieldCtaHeading: "Explore todos os estilos de citação",
    fieldCtaBody:
      "O CitationEasy oferece {count} estilos de citação em todas as áreas acadêmicas. Explore a coleção completa para encontrar o formato exato de que você precisa.",
    fieldCtaButton: "Ver todos os estilos →",
    fieldFaqWhichQ: "Qual estilo de citação é usado em {field}?",
    fieldFaqWhichA:
      "O estilo de citação usado em {field} depende do periódico, da instituição ou das exigências específicas do professor. Os estilos mais comuns incluem {styles}. Sempre confira as instruções do seu trabalho ou do periódico de destino para saber o formato exigido.",
    fieldFaqSameQ: "Todos os periódicos de {field} usam o mesmo estilo?",
    fieldFaqSameA:
      "Não, nem todos os periódicos de {field} usam o mesmo estilo de citação. Embora certos estilos sejam mais comuns na área, cada periódico pode ter suas próprias exigências de formatação. Sempre confira as normas de submissão do periódico antes de preparar seu manuscrito.",
    fieldFaqChooseQ:
      "Como escolho um estilo de citação para meu trabalho de {field}?",
    fieldFaqChooseA:
      "Para escolher um estilo de citação para seu trabalho de {field}, primeiro verifique se seu professor ou o periódico de destino exige um estilo específico. Se não, use um dos estilos comumente recomendados para {field} listados acima. O CitationEasy oferece mais de {count} estilos e pode gerar citações em qualquer um deles gratuitamente.",

    // ---- /citation-styles/country/<slug> ----
    countryNotFoundTitle: "País não encontrado",
    countryMetaTitle: "Estilos de citação de {country} - APA, MLA e mais",
    countryMetaDescription:
      "Explore os estilos de citação usados em {country}. Gerador de citações gratuito para os formatos de {country}.",
    countryOgTitle: "Estilos de citação usados em {country}",
    countryOgKind: "Por país",
    countryHeading: "Estilos de citação de {country}",
    countryIntroOne:
      "{count} estilo de citação comumente usado em {country} ou associado a este país. Escolha-o para abrir um gerador dedicado.",
    countryIntroMany:
      "{count} estilos de citação comumente usados em {country} ou associados a este país. Escolha um para abrir um gerador dedicado.",
    countryBrowserTitle: "Estilos de citação de {country}",

    // ---- /citation-styles/type/<slug> ----
    typeNotFoundTitle: "Tipo não encontrado",
    typeMetaTitle: "Estilos de citação de {type} - Gerador gratuito",
    typeMetaDescription:
      "Estilos de citação para {typeLower}. APA, MLA, Vancouver e mais. Gerador de citações gratuito.",
    typeOgTitle: "Estilos de citação de {type}",
    typeOgKind: "Por assunto",
    typeHeading: "Estilos de citação de {type}",
    typeIntroOne:
      "{count} estilo de citação para {typeLower}. Escolha-o para abrir um gerador dedicado com perguntas frequentes.",
    typeIntroMany:
      "{count} estilos de citação para {typeLower}. Escolha um para abrir um gerador dedicado com perguntas frequentes.",
    typeBrowserTitle: "Estilos de citação de {type}",

    // ---- Shared chrome ----
    breadcrumbStyles: "Estilos de citação",
    // Title case, because the BreadcrumbList markup says "Citation Styles" while the
    // visible trail says "Citation styles". Two keys rather than one so translating
    // either does not silently change the other.
    breadcrumbStylesSchema: "Estilos de Citação",
    breadcrumbByCountry: "Por país",
    breadcrumbByDiscipline: "Por disciplina",
    allStylesLink: "← Todos os estilos de citação",
    internationalLabel: "Internacional",
  },

  // The 32 academic fields (`ACADEMIC_FIELDS` in src/data/style-pages.ts), keyed by
  // slug. Their one-sentence `description` is next door in `academicFieldDescriptions`.
  academicFields: {
    psychology: "Psicologia",
    education: "Educação",
    sociology: "Sociologia",
    anthropology: "Antropologia",
    politicalScience: "Ciência política",
    economics: "Economia",
    business: "Administração e negócios",
    literature: "Literatura e línguas",
    history: "História",
    philosophy: "Filosofia",
    law: "Direito e estudos jurídicos",
    medicine: "Medicina e ciências clínicas",
    nursing: "Enfermagem e obstetrícia",
    publicHealth: "Saúde pública e epidemiologia",
    pharmacy: "Farmácia",
    biology: "Biologia e ciências da vida",
    chemistry: "Química",
    physics: "Física",
    engineering: "Engenharia",
    computerScience: "Ciência da computação",
    mathematics: "Matemática e estatística",
    environmentalScience: "Ciências ambientais",
    earthScience: "Ciências da Terra e geologia",
    artHistory: "História da arte e artes visuais",
    music: "Música e musicologia",
    theology: "Teologia e estudos religiosos",
    socialWork: "Serviço social",
    linguistics: "Linguística",
    architecture: "Arquitetura e urbanismo",
    filmMedia: "Cinema e estudos de mídia",
    journalism: "Jornalismo e comunicação",
    criminology: "Criminologia e justiça criminal",
  },

  // The same 32 fields' one-sentence ledes — the opening paragraph of every
  // `/citation-styles/field/<slug>` page.
  //
  // Unlike everything above, these are hand-written per field rather than mail-merged
  // from a template, which is why the extraction pass left them in
  // `src/data/style-pages.ts`: translating them is a content job, not a string swap.
  // But "hand-written" describes how they were produced, not where they belong. With
  // the chrome translated and the lede still English, the page is the hybrid CLAUDE.md
  // calls worse than an honest redirect — translated furniture around an English core.
  // `academicFieldDescription()` (src/lib/i18n/data-labels.ts) falls back to the data
  // file's own value, so a field added upstream is untranslated rather than blank.
  academicFieldDescriptions: {
    psychology:
      "Estilos de citação comumente usados em psicologia, ciências comportamentais e pesquisa cognitiva.",
    education:
      "Estilos de citação para pesquisa em educação, pedagogia e estudos curriculares.",
    sociology:
      "Estilos de citação usados em pesquisa sociológica e teoria social.",
    anthropology:
      "Estilos de citação para pesquisa antropológica e etnográfica.",
    politicalScience:
      "Estilos de citação para ciência política, governo e pesquisa em políticas públicas.",
    economics:
      "Estilos de citação usados em pesquisa econômica e econométrica.",
    business:
      "Estilos de citação para pesquisa em administração, gestão, marketing e finanças.",
    literature:
      "Estilos de citação para estudos literários, linguística e pesquisa sobre a linguagem.",
    history:
      "Estilos de citação para pesquisa histórica, historiografia e estudos de arquivo.",
    philosophy:
      "Estilos de citação para escrita filosófica e pesquisa em ética.",
    law: "Estilos de citação jurídica para documentos judiciais, revistas de direito e produção acadêmica jurídica.",
    medicine:
      "Estilos de citação para pesquisa médica, estudos clínicos e saúde.",
    nursing:
      "Estilos de citação para pesquisa em enfermagem e prática de saúde.",
    publicHealth:
      "Estilos de citação para pesquisa em saúde pública e estudos epidemiológicos.",
    pharmacy: "Estilos de citação para pesquisa farmacêutica e farmacológica.",
    biology: "Estilos de citação para pesquisa biológica, ecologia e genética.",
    chemistry:
      "Estilos de citação para química, bioquímica e pesquisa em ciência dos materiais.",
    physics:
      "Estilos de citação para física, astrofísica e pesquisa em física aplicada.",
    engineering:
      "Estilos de citação para engenharia e pesquisa em tecnologia aplicada.",
    computerScience:
      "Estilos de citação para ciência da computação, IA e pesquisa em engenharia de software.",
    mathematics:
      "Estilos de citação para matemática, matemática aplicada e pesquisa estatística.",
    environmentalScience:
      "Estilos de citação para pesquisa ambiental e estudos de sustentabilidade.",
    earthScience:
      "Estilos de citação para geologia, geofísica e pesquisa em ciências da Terra.",
    artHistory:
      "Estilos de citação para história da arte, cultura visual e estudos museológicos.",
    music:
      "Estilos de citação para pesquisa musical, teoria e estudos de performance.",
    theology:
      "Estilos de citação para produção acadêmica teológica e religiosa.",
    socialWork: "Estilos de citação para pesquisa e prática em serviço social.",
    linguistics:
      "Estilos de citação para linguística, fonética e ciência da linguagem.",
    architecture:
      "Estilos de citação para arquitetura, desenho urbano e pesquisa em planejamento.",
    filmMedia:
      "Estilos de citação para estudos de cinema, televisão, mídia e comunicação.",
    journalism:
      "Estilos de citação para jornalismo, estudos de mídia e pesquisa em comunicação.",
    criminology:
      "Estilos de citação para criminologia e pesquisa em justiça criminal.",
  },

  // The 68 country/region facets (`REGIONS` in src/data/citation-styles.ts), keyed by
  // slug. `getRegionCountryCode` still keys off the English data value, so translating these
  // moves the label without moving the flag.
  regions: {
    unitedStates: "Estados Unidos",
    unitedKingdom: "Reino Unido",
    australia: "Austrália",
    canada: "Canadá",
    newZealand: "Nova Zelândia",
    ireland: "Irlanda",
    southAfrica: "África do Sul",
    germany: "Alemanha",
    france: "França",
    spain: "Espanha",
    italy: "Itália",
    netherlands: "Países Baixos",
    belgium: "Bélgica",
    switzerland: "Suíça",
    austria: "Áustria",
    portugal: "Portugal",
    luxembourg: "Luxemburgo",
    sweden: "Suécia",
    norway: "Noruega",
    denmark: "Dinamarca",
    finland: "Finlândia",
    iceland: "Islândia",
    poland: "Polônia",
    czechRepublic: "República Tcheca",
    hungary: "Hungria",
    romania: "Romênia",
    russia: "Rússia",
    ukraine: "Ucrânia",
    greece: "Grécia",
    turkey: "Turquia",
    croatia: "Croácia",
    slovenia: "Eslovênia",
    slovakia: "Eslováquia",
    serbia: "Sérvia",
    bulgaria: "Bulgária",
    lithuania: "Lituânia",
    latvia: "Letônia",
    estonia: "Estônia",
    japan: "Japão",
    china: "China",
    southKorea: "Coreia do Sul",
    india: "Índia",
    singapore: "Singapura",
    hongKong: "Hong Kong",
    taiwan: "Taiwan",
    malaysia: "Malásia",
    indonesia: "Indonésia",
    thailand: "Tailândia",
    vietnam: "Vietnã",
    philippines: "Filipinas",
    pakistan: "Paquistão",
    bangladesh: "Bangladesh",
    sriLanka: "Sri Lanka",
    israel: "Israel",
    saudiArabia: "Arábia Saudita",
    unitedArabEmirates: "Emirados Árabes Unidos",
    brazil: "Brasil",
    mexico: "México",
    argentina: "Argentina",
    chile: "Chile",
    colombia: "Colômbia",
    peru: "Peru",
    venezuela: "Venezuela",
    egypt: "Egito",
    nigeria: "Nigéria",
    kenya: "Quênia",
    morocco: "Marrocos",
    international: "Internacional",
  },

  // The 39 subject categories (`CATEGORIES` in src/data/citation-styles.ts), keyed by
  // slug. The emoji beside each one stays in the data — it is language-neutral.
  styleCategories: {
    medicine: "Medicina",
    nursing: "Enfermagem",
    publicHealth: "Saúde pública",
    pharmacy: "Farmácia",
    dentistry: "Odontologia",
    veterinary: "Medicina veterinária",
    psychology: "Psicologia",
    sociology: "Sociologia",
    anthropology: "Antropologia",
    politicalScience: "Ciência política",
    economics: "Economia",
    education: "Educação",
    socialWork: "Serviço social",
    biology: "Biologia",
    chemistry: "Química",
    physics: "Física",
    environmentalScience: "Ciências ambientais",
    earthScience: "Ciências da Terra e geologia",
    agriculture: "Agricultura",
    ecology: "Ecologia",
    engineering: "Engenharia",
    computerScience: "Ciência da computação",
    mathematics: "Matemática",
    statistics: "Estatística",
    history: "História",
    literature: "Literatura",
    philosophy: "Filosofia",
    linguistics: "Linguística",
    artHistory: "História da arte",
    music: "Música",
    filmMedia: "Cinema e mídia",
    archaeology: "Arqueologia",
    law: "Direito",
    business: "Administração",
    architecture: "Arquitetura",
    religion: "Religião e teologia",
    geography: "Geografia",
    general: "Geral / Multidisciplinar",
    other: "Outro",
  },

  // The closed vocabularies `STYLE_LANDING_PAGES` (src/data/style-pages.ts) draws on for
  // the 30 major styles: what each style calls its end-of-paper list, the shape of its
  // in-text citation, its edition, and the disciplines that require it. The comparison
  // pages print all four in a table and the field pages print two of them under every
  // style card, so leaving them in the data means a translated page with an English
  // table in the middle of it.
  //
  // Style NAMES (`name`, `fullName`) are NOT here: a style's official name is what it
  // is called everywhere. The 30 hand-written taglines are, in `styleTaglines` below.
  styleFacts: {
    // `referenceListName` — 6 distinct values across the 30 styles.
    listReferenceList: "Lista de referências",
    listWorksCited: "Obras citadas",
    listReferences: "Referências",
    listBibliography: "Bibliografia",
    listTableOfAuthorities: "Tabela de autoridades",
    listReferencesCited: "Referências citadas",
    listLowerReferenceList: "lista de referências",
    listLowerWorksCited: "obras citadas",
    listLowerReferences: "referências",
    listLowerBibliography: "bibliografia",
    listLowerTableOfAuthorities: "tabela de autoridades",
    listLowerReferencesCited: "referências citadas",

    // `inTextFormat` — 14 distinct values. "Author", "Year" and "Page" are slot names,
    // not part of the citation, so they read as English words on a Spanish page.
    inTextAuthorCommaYear: "(Autor, Ano)",
    inTextAuthorPage: "(Autor Página)",
    inTextAuthorYear: "(Autor Ano)",
    inTextSuperscriptOrBracketed: "Números sobrescritos ou entre colchetes (1)",
    inTextBracketedNumbers: "Números entre colchetes [1]",
    inTextSuperscriptNumbers: "Números sobrescritos¹",
    inTextSuperscriptOrAuthorYear: "Números sobrescritos ou (Autor Ano)",
    inTextKeyBased: "Referências baseadas em chave",
    inTextSuperscriptOrAuthorCommaYear: "Números sobrescritos ou (Autor, Ano)",
    inTextFootnotesCaseLegislation:
      "Notas de rodapé com citações de jurisprudência e legislação",
    inTextFootnotesSignals: "Notas de rodapé com abreviações de sinalização",
    inTextFootnotesPinpoint: "Notas de rodapé com referências precisas",
    inTextParenNumbers: "Números entre parênteses (1)",
    inTextItalicNumbers: "Números em itálico (1)",

    // `edition` — 12 distinct values. The ordinal and the word "edition" translate; the
    // handbook name inside "Cite Them Right 11th edition" does not.
    edition4th: "4ª edição",
    edition5th: "5ª edição",
    edition7th: "7ª edição",
    edition8th: "8ª edição",
    edition9th: "9ª edição",
    edition11th: "11ª edição",
    edition18th: "18ª edição",
    edition22nd: "22ª edição",
    editionCiteThemRight11th: "Cite Them Right, 11ª edição",
    editionIcmjeStandard: "padrão ICMJE",
    editionStandard: "padrão",
    editionCurrent: "atual",

    // `fields` — the 67 discipline names the 30 styles are tagged with.
    fieldAcoustics: "acústica",
    fieldAnthropology: "antropologia",
    fieldAppliedPhysics: "física aplicada",
    fieldArchaeology: "arqueologia",
    fieldArts: "artes",
    fieldAstrophysics: "astrofísica",
    fieldAustralianLaw: "direito australiano",
    fieldBiochemistry: "bioquímica",
    fieldBiology: "biologia",
    fieldBiomedicalSciences: "ciências biomédicas",
    fieldBiotechnology: "biotecnologia",
    fieldBusiness: "administração",
    fieldCellBiology: "biologia celular",
    fieldChemicalEngineering: "engenharia química",
    fieldChemistry: "química",
    fieldClinicalResearch: "pesquisa clínica",
    fieldComputerScience: "ciência da computação",
    fieldCondensedMatter: "matéria condensada",
    fieldCourtDocuments: "documentos judiciais",
    fieldCriminology: "criminologia",
    fieldCulturalStudies: "estudos culturais",
    fieldDemography: "demografia",
    fieldEcology: "ecologia",
    fieldEducation: "educação",
    fieldElectronics: "eletrônica",
    fieldEngineering: "engenharia",
    fieldEnvironmentalScience: "ciências ambientais",
    fieldEpidemiology: "epidemiologia",
    fieldEthnography: "etnografia",
    fieldGenetics: "genética",
    fieldGeology: "geologia",
    fieldGlobalHealth: "saúde global",
    fieldHealthSciences: "ciências da saúde",
    fieldHistory: "história",
    fieldHumanities: "humanidades",
    fieldInformationSystems: "sistemas de informação",
    fieldInformationTechnology: "tecnologia da informação",
    fieldInternationalRelations: "relações internacionais",
    fieldJurisprudence: "jurisprudência",
    fieldLanguages: "línguas",
    fieldLaw: "direito",
    fieldLawReview: "revista jurídica",
    fieldLegalStudies: "estudos jurídicos",
    fieldLifeSciences: "ciências da vida",
    fieldLiterature: "literatura",
    fieldMaterialsScience: "ciência dos materiais",
    fieldMathematics: "matemática",
    fieldMedicine: "medicina",
    fieldMolecularBiology: "biologia molecular",
    fieldMultidisciplinary: "multidisciplinar",
    fieldMultidisciplinaryScience: "ciência multidisciplinar",
    fieldNaturalSciences: "ciências naturais",
    fieldNursing: "enfermagem",
    fieldPharmacy: "farmácia",
    fieldPhysics: "física",
    fieldPoliticalScience: "ciência política",
    fieldPsychology: "psicologia",
    fieldPublicHealth: "saúde pública",
    fieldPublicPolicy: "política pública",
    fieldPublishing: "publicação",
    fieldScience: "ciência",
    fieldSciences: "ciências",
    fieldSocialSciences: "ciências sociais",
    fieldSocialWork: "serviço social",
    fieldSociology: "sociologia",
    fieldSoftwareEngineering: "engenharia de software",
    fieldTheology: "teologia",
  },

  // The 30 major styles' hero taglines (`STYLE_LANDING_PAGES[].tagline`), keyed by
  // slug.
  //
  // Per-page hand-written copy, not a template — which is why they outlived the
  // extraction pass in `src/data/style-pages.ts`. Three page types print them: the
  // style landing page's hero, the card for each recommended style on a field
  // taxonomy page, and the lower-cased clause inside a comparison page's opening
  // sentence ("APA (…) is the most widely used citation style in the social
  // sciences"). Leaving them in the data means an English sentence in the first
  // paragraph of an otherwise translated page. `styleTagline()`
  // (src/lib/i18n/data-labels.ts) falls back to the data file's own value.
  styleTaglines: {
    apa: "O estilo de citação mais usado nas ciências sociais",
    mla: "O estilo de citação padrão para as humanidades",
    chicago:
      "O guia de estilo mais confiável dos Estados Unidos para autores, editores e editoras",
    harvard:
      "O estilo de referência mais popular nas universidades do Reino Unido e da Austrália",
    vancouver: "O estilo de citação padrão para revistas médicas e biomédicas",
    ieee: "O estilo de citação de referência para engenharia e ciência da computação",
    ama: "O padrão de citação das revistas médicas americanas",
    turabian: "A versão voltada para estudantes do estilo Chicago",
    cse: "O estilo de citação preferido nas ciências naturais e físicas",
    bibtex:
      "O formato de bibliografia para usuários de LaTeX e editoração acadêmica",
    nature:
      "O estilo de citação usado pela principal revista multidisciplinar do mundo",
    acs: "O padrão de citação para a química e as ciências químicas",
    oscola:
      "O estilo de citação jurídica padrão nas faculdades de direito e revistas do Reino Unido",
    bluebook:
      "O sistema de citação jurídica de referência nos tribunais e revistas de direito dos Estados Unidos",
    aglc: "O estilo de citação jurídica padrão nas faculdades de direito e tribunais da Austrália",
    asa: "O estilo de citação padrão para a sociologia e a pesquisa social",
    apsa: "O estilo de citação para a pesquisa e as revistas de ciência política",
    aaa: "O estilo de citação padrão para a pesquisa antropológica",
    nlm: "O padrão de citação usado pela PubMed e pela Biblioteca Nacional de Medicina dos EUA",
    acm: "O estilo de citação para as conferências da ACM e a pesquisa em computação",
    aps: "O estilo de citação da Physical Review e das revistas da APS",
    aip: "O estilo de citação do AIP e das revistas de física",
    cell: "O estilo de citação da Cell Press e das principais revistas de biologia",
    science: "O estilo de citação da revista Science e das publicações da AAAS",
    plos: "O estilo de citação das revistas de acesso aberto da PLOS",
    bmj: "O estilo de citação do BMJ e das principais revistas médicas britânicas",
    lancet: "O estilo de citação da família de revistas médicas The Lancet",
    jama: "O estilo de citação da JAMA e das revistas da JAMA Network",
    elsevierHarvard:
      "A variante do estilo Harvard usada nas revistas da Elsevier",
    springer: "O estilo de citação das revistas e livros da Springer Nature",
  },

  // The 105 style-versus-style pages at `/<locale>/compare/<slug>`.
  //
  // The two style names arrive as placeholder values and are never translated. The
  // facts in the table come from `styleFacts` above and the taglines from
  // `styleTaglines`; only `fullName` stays in `src/data/style-pages.ts`, because an
  // official name is the same in every language.
  comparePage: {
    notFoundTitle: "Não encontrado",
    metaTitle: "{a} vs. {b}: principais diferenças explicadas [2026]",
    metaDescription:
      "Compare os estilos de citação {a} e {b} lado a lado. Conheça as principais diferenças na formatação, nas citações no texto e nas listas de referências para escolher o estilo certo para o seu trabalho.",
    ogTitle: "{a} vs. {b}",
    ogSubtitle:
      "Formato no texto, entradas de referência, uso de maiúsculas e quais áreas exigem cada um.",
    ogKind: "Comparação de estilos",
    keywordVs: "{a} vs. {b}",
    keywordVersus: "{a} versus {b}",
    keywordDifference: "diferença entre {a} e {b}",
    keywordOr: "{a} ou {b}",
    keywordComparison: "comparação de estilos de citação",
    breadcrumbCompare: "Comparar estilos",
    breadcrumbCurrent: "{a} vs. {b}",
    backLink: "← Voltar para a ferramenta de comparação",
    heading: "{a} vs. {b}: principais diferenças",
    intro:
      "{a} e {b} são dois dos estilos de citação mais usados no meio acadêmico, mas diferem em aspectos importantes. {introA} {introB} Entender as diferenças vai ajudar você a escolher o formato certo para o seu trabalho.",
    styleIntro: "{style} ({fullName}) {tagline}.",
    styleIntroFallback:
      "{style} é um estilo de citação popular usado em muitas áreas acadêmicas.",
    tableHeading: "Comparação lado a lado",
    tableFeature: "Característica",
    tableCitationSystem: "Sistema de citação",
    tableInText: "Formato no texto",
    tableReferenceList: "Nome da lista de referências",
    tableEdition: "Edição",
    tableCommonFields: "Áreas comuns",
    // Title case, for the table cell. `styleLanding.system*` says "Author–date" for the
    // fact list on the style landing pages; both spellings are deliberate.
    systemAuthorDate: "Autor-Data",
    systemNumbered: "Numérico",
    systemFootnotes: "Notas de rodapé",
    // The same three systems inside a sentence ("uses a numbered citation system").
    describeAuthorDate: "autor-data entre parênteses",
    describeNumbered: "numérico",
    describeFootnotes: "baseado em notas de rodapé",
    whenToUseHeading: "Quando usar {style}",
    whenToUseBody:
      "{style} ({fullName}) usa um sistema de citação {system}, com citações no texto do tipo {inText} e uma lista final chamada «{list}». É usado principalmente em {fields}{more}.",
    whenToUseMoreFields: ", e áreas relacionadas",
    whenToUseFallback:
      "{style} é um estilo de citação amplamente reconhecido. Confira as instruções do seu trabalho ou o periódico de destino para confirmar se {style} é o formato exigido.",
    ctaHeading: "Veja a diferença na prática",
    ctaBodyBefore: "Experimente nossa",
    ctaBodyLink: "ferramenta de comparação interativa",
    ctaBodyAfter:
      "para comparar as citações em {a} e {b} lado a lado usando suas próprias fontes.",
    ctaCiteIn: "Citar em {style} →",
    faqDifferenceQ: "Qual é a diferença entre {a} e {b}?",
    faqDifferenceA:
      "{a} e {b} diferem no formato das citações no texto, na estrutura da lista de referências e nas áreas acadêmicas que os utilizam. {detail}",
    faqDifferenceDetail:
      "{a} usa citações no texto do tipo {inTextA} com uma lista chamada «{listA}», enquanto {b} usa {inTextB} com «{listB}».",
    faqDifferenceDetailFallback:
      "Cada estilo tem regras próprias para formatar citações, organizar bibliografias e lidar com diferentes tipos de fonte.",
    faqBetterQ: "Qual é melhor, {a} ou {b}?",
    faqBetterA:
      "Nem {a} nem {b} é inerentemente melhor. A escolha certa depende da sua área acadêmica, das exigências do seu professor ou do periódico ao qual você está submetendo o trabalho. Confira as instruções do seu trabalho ou a publicação de destino para determinar qual estilo usar.",
    faqSwitchQ: "Posso alternar entre {a} e {b}?",
    faqSwitchA:
      "Sim, você pode alternar entre {a} e {b} usando o gerador de citações gratuito do CitationEasy. Basta selecionar o estilo desejado e suas citações serão reformatadas automaticamente. Ainda assim, você deve usar apenas um estilo por trabalho, a menos que seu professor diga o contrário.",
  },

  // The ~21 converter-pair pages at `/<locale>/convert/<slug>`.
  //
  // Only the shell is here; each pair's own four strings are in `converterCopy` below.
  // The format names (DOI, ISBN, BibTeX, RIS, CSL-JSON, PubMed ID, arXiv ID, Word) are
  // identifiers, never translated.
  converterPage: {
    notFoundTitle: "Conversor não encontrado",
    metaTitle: "{title} - Gratuito e instantâneo",
    ogTitle: "{title}",
    ogKind: "Conversor",
    breadcrumbConverters: "Conversores",
    breadcrumbLabel: "Trilha de navegação",
    whenHeading: "Quando você precisa disso",
    relatedHeading: "Conversores relacionados",
    allConverters: "Todos os conversores",
    formatConverterTool: "Conversor de formato de citação",
    faqHowQ: "Como converto {input} para {output}?",
    faqHowA:
      "{intro} Tudo é gratuito, sem cadastro e sem limite de conversões.",
    faqPrivacyQ: "Meus dados são enviados para algum lugar?",
    faqPrivacyIdentifier:
      "Cada {input} é consultado no serviço de metadados público correspondente (Crossref para DOIs, PubMed para PMIDs, registros de bibliotecas para ISBNs), e apenas o identificador em si é enviado — nunca os seus documentos. Os resultados formatados são seus para copiar ou baixar; nada é armazenado.",
    faqPrivacyLocal:
      "Não — o {input} que você cola ou envia é processado inteiramente no seu navegador. {detail}",
    faqPrivacyDetailStyle:
      "Apenas os campos da referência já processados (autor, título, ano) são enviados ao nosso formatador para gerar as citações; o seu arquivo nunca sai do seu dispositivo.",
    faqPrivacyDetailOffline:
      "A conversão roda totalmente offline na página — nada é enviado a nenhum servidor.",
    faqUsefulQ: "Quando é útil converter de {input} para {output}?",
    faqOtherQ: "Posso converter para outros estilos e formatos também?",
    faqOtherA:
      "Sim. O gerador do CitationEasy formata qualquer fonte em {count} estilos de citação, a bibliografia é exportada em Word, RTF, BibTeX, RIS e CSL-JSON, e o catálogo de conversores lista todos os pares de conversão direta. Se você precisa reescrever um texto já formatado de um estilo para outro, use a ferramenta Conversor de formato de citação.",
  },

  // The 21 converter pairs' own copy (`CONVERTER_PAIRS` in src/data/converters.ts),
  // keyed by slug: title, meta description, the intro paragraph under the H1, and the
  // "when you need this" paragraph.
  //
  // Four hand-written strings per pair, and `src/data/converters.ts` has a quality rule
  // saying they must stay hand-written — no mail-merged sentences, because mass-templated
  // pages are what earned the site a scaled-content penalty. That rule is about how the
  // copy is authored; it says nothing about which language a reader gets it in. Left in
  // the data file they are the entire body of an otherwise translated page.
  // `converterPairCopy()` (src/lib/i18n/data-labels.ts) returns all four at once and
  // falls back to the data file per field, so a converter added upstream ships English
  // copy rather than a blank page.
  converterCopy: {
    doiToApaTitle: "Conversor de citações de DOI para APA",
    doiToApaMetaDescription:
      "Cole DOIs (um por linha) e receba referências em APA 7 na hora, extraídas dos metadados oficiais do Crossref. Grátis, sem conta.",
    doiToApaIntro:
      "Um DOI já aponta para os metadados oficiais da editora, então não há motivo para digitar uma referência em APA à mão. Cole um DOI por linha e cada um se transforma em uma referência APA 7 completa — autores invertidos, título em minúsculas estilo frase, revista em itálico e o link https://doi.org/ que a APA exige.",
    doiToApaUseCase:
      "Situação típica: seu professor pediu uma lista de referências em APA, e tudo que você tem é a pilha de DOIs que guardou durante a leitura. Cole a pilha, copie a lista.",
    doiToMlaTitle: "Conversor de citações de DOI para MLA",
    doiToMlaMetaDescription:
      "Transforme DOIs em entradas de obras citadas em MLA 9 automaticamente usando os metadados do Crossref. Cole um DOI por linha — grátis, sem instalar nada.",
    doiToMlaIntro:
      "O MLA 9 trata um artigo de revista como uma cadeia de contêineres — título do artigo entre aspas, revista em itálico, volume, número, ano, páginas — o que é chato de montar manualmente. Uma busca por DOI preenche cada campo a partir do registro da editora, então a entrada de obras citadas sai certa de primeira.",
    doiToMlaUseCase:
      "Prático quando uma disciplina de humanas exige MLA, mas suas fontes vêm de bases de dados voltadas para ciências que só oferecem DOIs.",
    doiToBibtexTitle: "Conversor de DOI para BibTeX",
    doiToBibtexMetaDescription:
      "Converta DOIs em entradas BibTeX para LaTeX e Overleaf. Cole um DOI por linha, baixe um .bib pronto — grátis e sem limites.",
    doiToBibtexIntro:
      "Todo trabalho em LaTeX acaba precisando do seu arquivo .bib. Cole os DOIs, um por linha, e receba entradas @article limpas com chaves de citação geradas automaticamente, prontas para o Overleaf, natbib ou biblatex — sem copiar campos manualmente das páginas da editora.",
    doiToBibtexUseCase:
      "O fluxo clássico: um revisor te manda três artigos para citar, você tem os DOIs deles, e o prazo é hoje à noite. Cole, baixe e use o \\cite.",
    doiToRisTitle: "Conversor de DOI para RIS",
    doiToRisMetaDescription:
      "Converta DOIs em um arquivo RIS que o Zotero, o Mendeley e o EndNote importam diretamente. Cole os DOIs um por linha — grátis, sem cadastro.",
    doiToRisIntro:
      "RIS é o formato de intercâmbio que todo gerenciador de referências importa. Cole os DOIs e baixe um arquivo .ris cujos campos TY/AU/TI/T2 vêm direto do Crossref — depois é só Arquivo → Importar no Zotero, Mendeley ou EndNote, e as entradas chegam totalmente estruturadas.",
    doiToRisUseCase:
      "Perfeito para migrar uma lista de DOIs coletada manualmente para um gerenciador de referências de verdade, sem redigitar um único nome de autor.",
    isbnToApaTitle: "Conversor de citações de ISBN para APA",
    isbnToApaMetaDescription:
      "Transforme ISBNs em referências de livros em APA 7 automaticamente. Cole um ISBN por linha e copie a lista formatada — grátis, sem conta.",
    isbnToApaIntro:
      "O ISBN na página de créditos de um livro é tudo o que você precisa para a referência em APA. Cada ISBN é consultado em metadados de bibliotecas e formatado no estilo APA 7 — autor, ano, título em itálico e editora —, incluindo a informação de edição quando o registro traz esse dado.",
    isbnToApaUseCase:
      "A forma mais rápida de citar uma pilha de livros físicos da biblioteca: vire cada um na página de créditos, digite os ISBNs, cole a lista aqui.",
    isbnToMlaTitle: "Conversor de citações de ISBN para MLA",
    isbnToMlaMetaDescription:
      "Converta ISBNs em entradas de obras citadas em MLA 9 em uma única colagem. Busca de ISBN grátis com saída já formatada — sem cadastro.",
    isbnToMlaIntro:
      "As entradas de livro em MLA 9 são curtas — autor, título em itálico, editora, ano — mas acertar a inversão do nome do autor e a pontuação ainda derruba muita gente. Uma busca por ISBN retorna o registro real do catálogo do livro e o formata de acordo com o modelo de elementos essenciais do manual MLA.",
    isbnToMlaUseCase:
      "Ideal para trabalhos de literatura: cite cada romance e cada monografia crítica da sua mesa digitando só os números da contracapa.",
    isbnToBibtexTitle: "Conversor de ISBN para BibTeX",
    isbnToBibtexMetaDescription:
      "Converta ISBNs em entradas BibTeX @book para LaTeX. Cole um ISBN por linha, baixe o arquivo .bib — grátis e instantâneo.",
    isbnToBibtexIntro:
      "Livros são o canto incômodo do BibTeX — sem DOI para buscar, e páginas de editoras raramente oferecem exportação em .bib. Este conversor resolve cada ISBN em metadados de bibliotecas e gera entradas @book com os campos de autor, título, editora e ano preenchidos.",
    isbnToBibtexUseCase:
      "Para quem está escrevendo uma tese: os capítulos de metodologia citam DOIs de artigos, mas o capítulo teórico cita doze livros. Isso resolve os doze livros.",
    pmidToApaTitle: "Conversor de citações de PMID para APA",
    pmidToApaMetaDescription:
      "Converta IDs do PubMed em referências APA 7 automaticamente. Cole os PMIDs um por linha e receba uma lista de referências formatada — grátis.",
    pmidToApaIntro:
      "Todo registro do PubMed tem um PMID, e os cursos de psicologia e enfermagem exigem APA na grande maioria das vezes. Cole os PMIDs um por linha e cada um é resolvido pelos próprios metadados do PubMed em uma referência de revista APA 7 completa, com o link do DOI incluído quando o registro tem um.",
    pmidToApaUseCase:
      "Feito para trabalhos de prática baseada em evidências: sua estratégia de busca termina em uma lista de PMIDs, e seu trabalho precisa deles em APA.",
    pmidToRisTitle: "Conversor de PMID para RIS",
    pmidToRisMetaDescription:
      "Transforme IDs do PubMed em um arquivo RIS para Zotero, Mendeley ou EndNote. Cole os PMIDs um por linha, baixe o .ris — grátis.",
    pmidToRisIntro:
      "Fluxos de revisão sistemática vivem em arquivos RIS. Cole os PMIDs da sua busca no PubMed e baixe um .ris pronto para sua ferramenta de triagem ou gerenciador de referências — cada registro estruturado a partir dos metadados canônicos do PubMed, e não de texto extraído.",
    pmidToRisUseCase:
      "Útil no meio de uma revisão, quando um colega te manda um e-mail dizendo 'inclua também esses cinco estudos' com PMIDs soltos, e sua biblioteca vive no EndNote.",
    arxivToBibtexTitle: "Conversor de arXiv para BibTeX",
    arxivToBibtexMetaDescription:
      "Converta IDs do arXiv em entradas BibTeX para trabalhos em LaTeX. Cole IDs como 2301.12345, um por linha, e baixe o .bib — grátis.",
    arxivToBibtexIntro:
      "Preprints são citados o tempo todo em áreas que avançam rápido, e os IDs do arXiv (como 2301.12345) são como eles circulam. Cole os IDs e receba entradas BibTeX com os autores, o título, o ano e o link do arXiv do preprint — prontas para o arquivo .bib do seu trabalho.",
    arxivToBibtexUseCase:
      "Para quem escreve em machine learning e física, cuja seção de trabalhos relacionados é 80% arXiv: colete os IDs enquanto lê e converta tudo de uma vez na hora de escrever.",
    bibtexToApaTitle: "Conversor de BibTeX para APA",
    bibtexToApaMetaDescription:
      "Cole ou envie um arquivo .bib e receba uma lista de referências em APA 7. Funciona com arquivos de várias entradas — grátis, processado no seu navegador.",
    bibtexToApaIntro:
      "Às vezes o trabalho sai do LaTeX: uma revista pede uma submissão em Word, ou uma banca quer a lista de referências em texto simples. Cole seu BibTeX (ou envie o .bib) e cada entrada é processada no seu navegador e formatada como uma lista de referências APA 7 que você pode colar em qualquer documento.",
    bibtexToApaUseCase:
      "O especial de solicitação de bolsa: sua lista de publicações vive em um arquivo .bib, e o formulário do financiador quer referências formatadas em APA em uma caixa de texto.",
    bibtexToMlaTitle: "Conversor de BibTeX para MLA",
    bibtexToMlaMetaDescription:
      "Converta BibTeX em entradas de obras citadas em MLA 9. Cole ou envie o arquivo .bib e copie a lista formatada — grátis, sem conta.",
    bibtexToMlaIntro:
      "O BibTeX armazena campos brutos; o MLA determina exatamente como organizá-los. Este conversor processa suas entradas .bib localmente e renderiza cada uma pelo modelo MLA 9 — inversão do autor, títulos de artigo entre aspas, contêineres em itálico e a pontuação correta entre os elementos.",
    bibtexToMlaUseCase:
      "Para pesquisadores que transitam entre áreas: suas referências vivem em BibTeX de um trabalho anterior em LaTeX, mas a revista de humanas para a qual você está submetendo quer MLA.",
    bibtexToRisTitle: "Conversor de BibTeX para RIS",
    bibtexToRisMetaDescription:
      "Converta um arquivo .bib para RIS destinado a Zotero, Mendeley ou EndNote. Cole ou envie o BibTeX, baixe o .ris — grátis e offline.",
    bibtexToRisIntro:
      "Migrar das ferramentas de LaTeX para um gerenciador de referências significa traduzir as entradas @article/@book do BibTeX para as tags TY/AU/TI do RIS. A conversão acontece inteiramente no seu navegador — com o mapeamento de campos incluído — e entrega um arquivo .ris que qualquer gerenciador importa.",
    bibtexToRisUseCase:
      "Gatilho comum: um novo coautor trabalha no Zotero, as referências compartilhadas de vocês são um .bib no repositório, e alguém precisa fazer a ponte.",
    bibtexToCslJsonTitle: "Conversor de BibTeX para CSL-JSON",
    bibtexToCslJsonMetaDescription:
      "Converta BibTeX para CSL-JSON destinado ao Pandoc e ao Quarto. Cole ou envie o .bib, baixe o references.json — grátis, no navegador.",
    bibtexToCslJsonIntro:
      "O Pandoc e o Quarto citam nativamente a partir de CSL-JSON, e converter seu .bib existente remove uma peça móvel da build. As entradas são processadas no seu navegador e saem como um array CSL-JSON limpo — exatamente o formato que --bibliography=references.json espera.",
    bibtexToCslJsonUseCase:
      "Para acadêmicos que trabalham primeiro em Markdown e estão convertendo um projeto antigo de LaTeX para o Quarto: leve a bibliografia junto sem editar JSON manualmente.",
    bibtexToWordTitle: "Conversor de BibTeX para Word",
    bibtexToWordMetaDescription:
      "Transforme um arquivo .bib em um documento do Word com uma lista de referências em APA e recuo francês. Grátis — o processamento acontece no seu navegador.",
    bibtexToWordIntro:
      "Quando um arquivo .bib precisa virar um documento — e não outro formato de dados — este conversor processa seu BibTeX localmente, formata cada entrada em APA 7 e baixa um arquivo do Word com a lista de referências já em ordem alfabética e com recuo francês.",
    bibtexToWordUseCase:
      "A jogada de fim de linha: a revista aceitou o artigo, mas quer a lista de referências final como um arquivo do Word editável, e sua fonte da verdade é o BibTeX.",
    risToApaTitle: "Conversor de RIS para APA",
    risToApaMetaDescription:
      "Cole ou envie uma exportação RIS e receba uma lista de referências em APA 7. Funciona com Zotero, Mendeley, EndNote e exportações de bases de dados — grátis.",
    risToApaIntro:
      "O botão Exportar de qualquer base de dados gera RIS; os trabalhos pedem APA. Cole a exportação (ou envie o .ris) e cada registro TY/AU/TI é processado no seu navegador e renderizado como uma referência APA 7 — sem precisar instalar nenhum gerenciador de referências.",
    risToApaUseCase:
      "O atalho da base de dados da biblioteca: exporte seus resultados de busca como RIS, converta aqui e pule a configuração do Zotero para um trabalho pontual.",
    risToMlaTitle: "Conversor de RIS para MLA",
    risToMlaMetaDescription:
      "Converta exportações RIS de bases de dados ou gerenciadores de referências em entradas de obras citadas em MLA 9. Cole ou envie — grátis, sem cadastro.",
    risToMlaIntro:
      "JSTOR, ProQuest e EBSCO exportam tudo em RIS, e os departamentos de letras querem tudo em MLA. Esta página fecha essa lacuna: os registros são processados localmente e renderizados pelo modelo de elementos essenciais do MLA 9, prontos para colar na sua página de obras citadas.",
    risToMlaUseCase:
      "Feito para a noite antes da entrega do ensaio: dez fontes exportadas do JSTOR como citations.ris, e nenhuma vontade de formatá-las à mão.",
    risToBibtexTitle: "Conversor de RIS para BibTeX",
    risToBibtexMetaDescription:
      "Converta RIS para BibTeX destinado a LaTeX e Overleaf. Cole ou envie a exportação .ris, baixe um arquivo .bib limpo — grátis, no navegador.",
    risToBibtexIntro:
      "Indo na direção do LaTeX: gerenciadores de referências e bases de dados falam RIS, e a build do seu trabalho precisa de .bib. Os registros são convertidos inteiramente no seu navegador — com tipos mapeados a partir das tags TY e chaves de citação geradas — em um arquivo que o Overleaf aceita como está.",
    risToBibtexUseCase:
      "Comum em colaborações: quem usa Zotero exporta RIS, quem usa LaTeX passa pela página, e cada um mantém sua ferramenta.",
    risToCslJsonTitle: "Conversor de RIS para CSL-JSON",
    risToCslJsonMetaDescription:
      "Converta exportações RIS para CSL-JSON destinado a fluxos do Pandoc e do Quarto. Cole ou envie o .ris, baixe o references.json — grátis.",
    risToCslJsonIntro:
      "Para quem usa Pandoc e Quarto, o CSL-JSON é o formato de bibliografia nativo. Dê a esta página o RIS exportado pela sua base de dados ou gerenciador e receba de volta um array JSON estruturado — cada registro processado localmente, nada enviado para lugar nenhum.",
    risToCslJsonUseCase:
      "Encaixa em fluxos de docs-as-code: revisores mandam anexos RIS, o arquivo de citações do seu repositório é references.json, e esta página é o adaptador.",
    cslJsonToBibtexTitle: "Conversor de CSL-JSON para BibTeX",
    cslJsonToBibtexMetaDescription:
      "Converta CSL-JSON (exportações do Pandoc, Quarto ou Zotero) para BibTeX. Cole ou envie o .json, baixe o .bib — grátis, no navegador.",
    cslJsonToBibtexIntro:
      "Às vezes o fluxo se inverte: uma bibliografia nascida em um pipeline do Pandoc ou do Zotero precisa alimentar uma build em LaTeX. Este conversor lê o array CSL-JSON no seu navegador e gera entradas BibTeX com tipos e campos mapeados de volta a partir do modelo CSL.",
    cslJsonToBibtexUseCase:
      "Para o autor que trabalha no Quarto mas tem um coautor que insiste no Overleaf: mantenha o references.json como fonte canônica e gere o .bib sob demanda.",
    cslJsonToRisTitle: "Conversor de CSL-JSON para RIS",
    cslJsonToRisMetaDescription:
      "Converta CSL-JSON para RIS para que Zotero, Mendeley ou EndNote possam importar. Cole ou envie o .json, baixe o .ris — grátis.",
    cslJsonToRisIntro:
      "O CSL-JSON é ótimo dentro de pipelines do Pandoc e opaco para as caixas de importação da maioria dos gerenciadores de referências. Converter para RIS torna os mesmos registros universalmente importáveis — cada item mapeado para as tags TY/AU/TI no seu navegador, nada enviado a um servidor.",
    cslJsonToRisUseCase:
      "O problema da entrega: sua bibliografia é um references.json em um repositório git, e seu orientador quer isso 'no EndNote' até sexta-feira.",
  },

  // The infinite-scroll style list shared by the country and subject pages and by the
  // /citation-styles hub. It is a client component, so these arrive as a prop rather
  // than through `getDictionary` — pulling all 17 dictionaries into the browser bundle
  // to label a search box is the thing `link-path.ts` exists to avoid.
  stylesBrowser: {
    defaultTitle: "Todos os estilos de citação",
    styleSingular: "estilo",
    stylePlural: "estilos",
    found: "da busca",
    filterPlaceholder: "Filtrar estilos...",
    noMatches: "Nenhum estilo corresponde à sua busca.",
    loadingMore: "Carregando mais estilos...",
    loadMore: "Carregar mais ({count} restantes)",
    showingAll: "Mostrando todos os {count} estilos",
    internationalLabel: "Internacional",
  },

  footer: {
    tagline:
      "Um gerador de referências gratuito com {count} estilos. Sem conta, sem assinatura paga e sem limites.",
    freeTools: "Ferramentas gratuitas",
    resources: "Recursos",
    legal: "Jurídico",
    language: "Idioma",
    languageLabel: "Escolha seu idioma",
    rights:
      "As citações são formatadas com as definições oficiais da Citation Style Language.",
    popularStyles: "Estilos populares",
    about: "Sobre",
    privacy: "Privacidade",
    terms: "Termos",
    contact: "Contato",
    copyright: "© {year} CitationEasy. Uso gratuito.",
    poweredBy: "Desenvolvido com",
    allTools: "Todas as ferramentas de escrita",
    citationGuides: "Guias de citação",
    allStyles: "Todos os estilos de citação",
    citationGlossary: "Glossário de citação",
    howToCiteAny: "Como citar qualquer fonte",
  },

  faq: {
    heading: "Perguntas frequentes",
    homeQ1: "O CitationEasy é mesmo gratuito?",
    homeA1:
      "Sim. O CitationEasy é totalmente gratuito: sem cadastro, sem limites e sem versão paga. Todos os {count} estilos de citação estão disponíveis para qualquer pessoa.",
    homeQ2: "Quais estilos de citação são aceitos?",
    homeA2:
      "Todos os {count} estilos do projeto Citation Style Language, incluindo APA 7ª edição, MLA 9ª edição, Chicago, Harvard, Vancouver, IEEE, AMA e Turabian.",
    homeQ3: "Preciso de uma conta?",
    homeA3:
      "Não. O CitationEasy funciona inteiramente no seu navegador e a sua bibliografia fica salva no seu próprio dispositivo. Nada é enviado para servidores e não há cadastro.",
    homeQ4: "Posso gerar as referências no meu idioma?",
    homeA4:
      "Sim. O idioma das referências é uma configuração separada do idioma do site, então você pode trabalhar no seu idioma e gerar as referências no idioma em que o trabalho está escrito.",
    styleQ1: "O gerador de citações {style} é gratuito?",
    styleA1:
      "Sim. Gerar citações em {style} é gratuito e ilimitado, sem necessidade de conta. Cole um DOI, uma URL, um ISBN ou uma entrada BibTeX e copie a referência pronta.",
    styleQ2: "Como citar uma fonte em {style}?",
    styleA2:
      "Cole o DOI, a URL, o ISBN ou a entrada BibTeX da fonte no gerador desta página. O estilo {style} já vem selecionado, então a referência e a citação no texto aparecem na hora.",
    styleQ3: "Qual é a precisão da formatação {style}?",
    styleA3:
      "Cada referência é construída a partir da definição oficial de {style} publicada pelo projeto Citation Style Language — o mesmo arquivo usado por gerenciadores como Zotero e Mendeley — e não de uma aproximação das regras.",
  },

  tool: {
    modeLabel: "Modo de entrada da referência",
    modeAuto: "URL / DOI / ISBN",
    modeBook: "Buscar livros",
    modeArticles: "Buscar artigos",
    modeManual: "Entrada manual",
    errorEnterManually: "Inserir os dados manualmente",
    autoLabel: "Cole uma URL, DOI, PMID, ID do arXiv, ISBN ou BibTeX",
    autoHint:
      "Detectamos o tipo automaticamente e buscamos os metadados. Cole um identificador por linha para citar uma lista inteira de uma vez.",
    generate: "Gerar referência",
    generating: "Gerando…",
    bookSearchLabel: "Busque por título, autor ou ISBN",
    bookSearchEmpty:
      "Ainda sem resultados. Clique em Buscar para encontrar livros.",
    bookSearchSource:
      "Busca no catálogo da Open Library, com milhões de livros",
    worksSearchLabel: "Busque por título do artigo ou autores",
    worksSearchEmpty:
      "Ainda sem resultados. Clique em Buscar para encontrar artigos.",
    worksSearchSource:
      "Busca no catálogo da Crossref: artigos de periódicos, trabalhos de congresso e capítulos de livro",
    showingResults: "Exibindo {shown} de {total} resultados",
    search: "Buscar",
    searching: "Buscando…",
    bulkResults: "Resultados da citação em lote",
    copy: "Copiar",
    copied: "Copiado!",
    copyHint: "Copie e cole no seu trabalho",
    resultTitle: "Referência formatada",
    edit: "Editar",
    completeFields: "Completar",
    fieldsIncomplete:
      "one:{count} campo incompleto|other:{count} campos incompletos",
    outputLanguage: "Idioma",
    sourceType: "Tipo de fonte",
    fieldAuthors: "Autor(es)",
    fieldAuthorsHint: "Silva, João ou João Silva",
    fieldTitle: "Título do artigo ou livro",
    fieldBookTitle: "Título do livro",
    fieldBookTitleHint: "Título do livro ou da coletânea",
    fieldEditors: "Organizador(es)",
    fieldJournal: "Periódico / publicação",
    fieldJournalHint: "Nome do periódico ou publicação",
    fieldPublisher: "Editora / site",
    fieldPublisherHint: "Nome da editora ou site",
    fieldDate: "Data / ano",
    fieldVolume: "Volume",
    fieldIssue: "Número",
    fieldPages: "Página(s)",
    fieldFormat: "Formato / suporte",
    fieldAuthorsGroup: "Autor coletivo ou organização",
    fieldAuthorsGroupHint:
      "Para listar um grupo junto com pessoas, coloque-o entre chaves: {R Core Team}, João Silva",
    fieldTranslators: "Tradutor(es)",
    fieldInterviewers: "Entrevistador(es)",
    fieldVersion: "Versão",
    fieldCourt: "Tribunal",
    fieldIssuingAuthority: "Autoridade emissora",
    fieldPatentNumber: "Número da patente",
    fieldEpisodeNumber: "Número do episódio",
    fieldLawNumber: "Número da lei ou do projeto de lei",
    fieldReportNumber: "Número do relatório",
    fieldReviewedTitle: "Título da obra resenhada",
    fieldReviewedAuthors: "Autor(es) da obra resenhada",
    inTextCitation: "Citação no texto",
    quickEdit: "Edição rápida",
    quickEditHint: "Atualize os metadados e gere a referência novamente.",
    update: "Atualizar",
    saving: "Salvando…",
    fieldAuthor: "Autor",
    fieldDateShort: "Data",
    fieldTitleShort: "Título",
  },

  toolCollections: {
    "citation-generators": "Geradores de citações",
    "citation-tools": "Ferramentas de citação e referências",
    "paper-formatting": "Formatação de trabalhos",
    "essay-writing": "Redação de ensaios e trabalhos",
    "writing-analysis": "Análise e revisão de textos",
    "word-counters": "Contadores e legibilidade",
    vocabulary: "Vocabulário e busca de palavras",
    "research-tools": "Pesquisa e publicação",
    statistics: "Calculadoras de estatística",
    math: "Calculadoras de matemática",
    science: "Calculadoras de ciências e laboratório",
    charts: "Gráficos e diagramas",
    economics: "Economia, negócios e finanças",
    grades: "Calculadoras de GPA e notas",
    study: "Estudo e produtividade",
    classroom: "Sala de aula e material para imprimir",
    random: "Geradores e sorteadores aleatórios",
    "text-tools": "Limpeza e formatação de texto",
    "fancy-text": "Texto estilizado e símbolos",
    language: "Idiomas e transliteração",
    ciphers: "Códigos e cifras",
    converters: "Conversores de unidades, datas e números",
    developer: "Dados, marcação e código",
  },
  bib: {
    title: "Minhas bibliografias",
    studioTitle: "Estúdio de bibliografia",
    studioProjects: "Projetos",
    studioExplore: "Explorar",
    studioExploreIntro:
      "Guias, ferramentas e todos os estilos de citação a um clique.",
    studioStyleGuide: "Guia do estilo {style}",
    studioStyleGuideHint:
      "Regras, exemplos resolvidos e formato da citação no texto",
    studioGenerator: "Gerador de citações",
    studioNewProject: "Novo projeto",
    studioSwitchProject: "Trocar de projeto",
    studioAddSource: "Adicionar uma fonte",
    studioAddSourceHint:
      "Cole um DOI, URL ou ISBN, pesquise livros e artigos ou insira os dados manualmente. Tudo o que você salvar entra neste projeto.",
    studioDoneAdding: "Concluir",
    studioReferences: "Referências",
    studioReferenceCount: "{count} referências",
    studioInspectorTitle: "Detalhes da referência",
    studioInspectorEmpty:
      "Selecione uma referência para ver a citação no texto, as notas e os detalhes da fonte.",
    studioSavedLocally: "Salvo neste navegador",
    studioOpen: "Abrir o Estúdio de bibliografia",
    studioViewInStudio: "Ver no Estúdio",
    studioFooterSaved: "{count} salvas no seu Estúdio de bibliografia",
    studioFooterEmpty:
      "As citações salvas formam uma bibliografia completa no Estúdio",
    studioClearSelection: "Limpar seleção",
    studioDeleteSelected: "Excluir selecionadas",
    studioDeleteSelectedBody:
      "Excluir {count} citações de “{name}”? Esta ação não pode ser desfeita.",
    studioDeletedMany: "{count} citações excluídas",
    studioMoveTo: "Mover para",
    studioCopyTo: "Copiar para",
    studioSearchShortcut: "Pressione / para pesquisar",
    studioStyle: "Estilo",
    studioCopyEntry: "Copiar entrada",
    studioEntryCopied: "Entrada copiada",
    studioSourceDetails: "Detalhes da fonte",
    studioEmptyTitle: "Este projeto está vazio",
    studioEmptyBody:
      "Adicione sua primeira fonte acima ou importe uma biblioteca BibTeX, RIS ou CSL-JSON existente.",
    studioBackup: "Backup",
    sharedTitle: "Bibliografia compartilhada",
    save: "Salvar",
    saving: "Salvando…",
    saved: "Salvo",
    searchPlaceholder: "Buscar referências…",
    empty: "Nenhuma referência salva ainda",
    emptySearch: "Nenhuma referência corresponde à sua busca.",
    countIn: "Referências em {name}: {count}",
    savedOn: "Salva em {date}",
    emptyInBib:
      "Ainda não há referências nesta bibliografia. Gere e salve referências, ou importe uma biblioteca BibTeX, RIS ou CSL-JSON.",
    savedToast: "Referência salva na bibliografia",
    copyAll: "Copiar tudo",
    copyAllTitle: "Copiar todas as referências",
    export: "Exportar",
    exportTitle: "Exportar a bibliografia",
    import: "Importar",
    importTitle: "Importar referências",
    importLabel: "Importar referências (BibTeX, RIS ou CSL-JSON)",
    importing: "Importando…",
    importFailed: "A importação falhou. Tente novamente.",
    importSummary:
      "Importadas: {added} · Duplicatas ignoradas: {duplicates} · Acima do limite: {overLimit} · Falhas: {failed}",
    importUnreadable: "Não foi possível ler o arquivo.",
    importUnparseable: "Não foi possível importar as referências.",
    importReviewTitle: "Rever importação",
    importReviewIntro:
      "Reveja {total} referências {format} antes de as formatar e guardar.",
    importReviewCount: "{included} de {total} incluídas",
    importSelectAll: "Incluir todas",
    importExcludeAll: "Excluir todas",
    importLimitNotice:
      "{count} referências acima do limite de 200 entradas não serão importadas.",
    importDuplicateExisting: "Possível duplicado de “{title}”, já guardada",
    importDuplicateBatch: "Possível duplicado de “{title}” nesta importação",
    importMissingField: "{field} em falta",
    importUntitled: "Referência sem título",
    importReview: "Rever referências",
    importCommit: "Formatar e importar",
    importBack: "Voltar",
    importOutcomeTitle: "Importação concluída",
    importOutcomeSummary:
      "Adicionadas: {added} · Duplicados ignorados: {duplicates} · Excluídas: {excluded} · Acima do limite: {overLimit} · Falhas: {failed}",
    sortBy: "Ordenar por",
    sortTitle: "Ordenar a bibliografia",
    sortAuthor: "Autor (A–Z)",
    sortNewest: "Data de inclusão (mais recente)",
    sortOldest: "Ano (mais antigo primeiro)",
    sortManual: "Ordem manual",
    backupAll: "Cópia de segurança completa (.json)",
    backupDownloaded: "Cópia de segurança completa transferida",
    backupUnreadable: "Não foi possível ler o ficheiro de cópia de segurança.",
    restore: "Restaurar",
    restoreTitle: "Restaurar todos os projetos bibliográficos",
    restoreWarning:
      "A cópia será validada primeiro e depois substituirá todas as bibliografias guardadas neste navegador. Esta ação não pode ser anulada.",
    restoreChoose: "Escolha uma cópia do CitationEasy (.json)",
    replaceAndRestore: "Substituir e restaurar",
    restoreComplete: "Todos os projetos bibliográficos foram restaurados",
    restoreInvalid: "Esta não é uma cópia de projeto válida do CitationEasy.",
    selectAllVisible: "Selecionar tudo o que é mostrado",
    selectedCount: "{count} selecionadas",
    clusterOpen: "Citar juntas",
    clusterTitle: "Citação composta no texto",
    clusterDescription:
      "Crie um único grupo de citações com as referências selecionadas. O estilo escolhido controla a ordem, a pontuação e a numeração.",
    clusterTarget: "Saída",
    clusterLocatorOptional: "Localizador opcional",
    clusterBuildCopy: "Criar e copiar citação",
    clusterBuilding: "Criando citação…",
    clusterCopied: "Citação composta copiada",
    clusterFailed:
      "Não foi possível criar a citação composta. Tente novamente.",
    destination: "Bibliografia de destino",
    chooseDestination: "Escolha uma bibliografia…",
    copySelected: "Copiar",
    moveSelected: "Mover",
    copyComplete:
      "{count} citações copiadas. {skipped} duplicados foram ignorados.",
    moveComplete:
      "{count} citações movidas. {skipped} duplicados permaneceram na origem.",
    selectCitation: "Selecionar {title}",
    moveUp: "Mover para cima",
    moveDown: "Mover para baixo",
    deleteTitle: "Excluir referência",
    deleteConfirm: "Excluir a referência?",
    deleted: "Referência excluída",
    editTitle: "Editar referência",
    close: "Fechar",
    copiedAll: "Todas as referências copiadas!",
    copiedAnnotated: "Bibliografia anotada copiada!",
    copiedBibtex: "BibTeX copiado!",
    copiedBulleted: "Lista com marcadores copiada!",
    copiedNumbered: "Lista numerada copiada!",
    copiedHtml: "Trecho HTML copiado!",
    downloadedWord: "Documento do Word baixado",
    downloadedRtf: "Arquivo RTF baixado",
    downloadedRis: "Arquivo RIS baixado",
    downloadedCsl: "Arquivo CSL-JSON baixado",
    downloadedAnnotated: "Bibliografia anotada baixada",
    copyFailed: "Não foi possível copiar para a área de transferência",
    shareCopied:
      "Link copiado! Quem tiver o link pode ver e importar esta lista.",
    shareFailed: "Não foi possível criar o link de compartilhamento",
    regenerateFailed: "Não foi possível gerar a referência novamente",
    updateFailed: "Não foi possível atualizar a referência",
    fieldAuthor: "Autor",
    fieldTitle: "Título",
    fieldDate: "Data",
    fieldPublisher: "Editora",
    fieldVolume: "Volume",
    fieldIssue: "Número",
    fieldPages: "Página(s)",
    fieldUrl: "URL",
    fieldAccessed: "Data de acesso",
    fieldEditor: "Editor",
    fieldEvent: "Local do evento",
    noteLabel: "Anotação sobre esta fonte",
    noteEdit: "Editar esta nota",
    notePlaceholder:
      "Resuma a fonte, avalie-a e diga como ela se encaixa no seu trabalho…",
    noteRemoved: "Nota removida",
    noteSave: "Salvar nota",
    noteCancel: "Cancelar",
    noteAdd: "Adicionar nota",
    noteWords: "{count} palavras",
    noteSaved: "Nota salva ({count} palavras)",
    noteClickToEdit: "clique para editar",
    inTextCopy: "Copiar a citação no texto desta fonte",
    inTextSelect: "Selecione a citação abaixo para copiá-la.",
    inTextFailed:
      "Não foi possível montar a citação no texto. Tente novamente.",
    locator: "Localizador",
    locatorType: "Tipo de localizador",
    locatorPage: "Página",
    locatorChapter: "Capítulo",
    locatorSection: "Seção",
    locatorParagraph: "Parágrafo",
    locatorLine: "Linha",
    locatorVerse: "Versículo",
    reformat: "Reformatar",
    reformatTitle: "Reformatar todas as referências em outro estilo",
    reformatHeading: "Reformatar tudo em um só estilo",
    reformatChoose: "Escolha um estilo…",
    reformatFailed: "A reformatação falhou. Sua bibliografia não foi alterada.",
    sortOrder: "Critério de ordenação",
    copyToClipboard: "Copiar para a área de transferência",
    numberedList: "Lista numerada",
    bulletedList: "Lista com marcadores",
    htmlSnippet: "Trecho HTML",
    copyWithAnnotations: "Copiar com anotações",
    wordAnnotated: "Documento do Word anotado (.doc)",
    downloadFile: "Baixar arquivo",
    wordDocument: "Documento do Word (.doc)",
    copyShareLink: "Copiar link de compartilhamento",
    print: "Imprimir…",
    printFailed: "Pop-up bloqueado — permita pop-ups para imprimir",
    font: "Fonte",
    sitePublication: "Site / nome da publicação",
    importIntro:
      "Cole ou envie um arquivo BibTeX (.bib), RIS (.ris) ou CSL-JSON (.json)",
    importUpload: "Envie um arquivo .bib, .ris ou .json",
    importPaste: "Ou cole BibTeX, RIS ou CSL-JSON",
    cancel: "Cancelar",
    done: "Concluído",
    delete: "Excluir",
    deleteBody:
      "Isto removerá esta referência da sua bibliografia permanentemente.",
    editBody:
      "Atualize os metadados e salve para gerar a referência novamente.",
    reformatExplain:
      "Reconstrói as {count} entradas a partir dos dados salvos — nada é baixado de novo e nenhuma fonte é perdida.",
    inTextHeading: "Citação no texto",
    inTextLocatorHint:
      "Adicione uma página ou outro localizador para fixar a citação em um ponto da fonte — a maioria dos estilos exige isso em citações diretas. Deixe em branco para a forma simples.",
    inTextCopyButton: "Copiar a citação no texto",
    mixedStyles:
      "Esta bibliografia mistura {count} estilos de citação ({styles}).",
    mixedStylesAction:
      "Use {action} para deixar tudo em um só estilo antes de entregar.",
    selectBibliography: "Selecionar bibliografia",
    newBibliography: "Nova bibliografia",
    moreOptions: "Mais opções",
    rename: "Renomear",
    removeDuplicates: "Remover duplicatas",
    cannotDeleteOnly: "Não é possível excluir sua única bibliografia",
    newBibliographyBody:
      "Crie uma bibliografia para outro trabalho ou projeto.",
    bibliographyName: "Nome da bibliografia",
    create: "Criar",
    renameBibliography: "Renomear bibliografia",
    renameBibliographyBody: "Digite um novo nome para “{name}”.",
    newName: "Novo nome",
    duplicateRemovedOne: "{count} duplicata removida",
    duplicateRemovedMany: "{count} duplicatas removidas",
    noDuplicates: "Nenhuma referência duplicada encontrada",
    removeDuplicatesConfirm: "Remover referências duplicadas?",
    removeDuplicatesBody:
      "Esta ação procura em “{name}” a mesma fonte (por DOI, ISBN ou título, ano e autor) e mantém uma cópia. As referências que não puderem ser comparadas não serão alteradas.",
    deleteBibliographyConfirm: "Excluir bibliografia?",
    deleteBibliographyBody:
      "Isso excluirá permanentemente “{name}” e todas as suas {count} citações.",
  },

  bibliographyPage: {
    metaTitle: "Estúdio de bibliografia - organize, anote e exporte citações",
    metaDescription:
      "Crie e gerencie projetos bibliográficos privados no navegador. Pesquise, ordene, anote, edite, reformate, importe, exporte e compartilhe citações sem uma conta.",
    keyword1: "criador de bibliografia",
    keyword2: "gerenciador de citações",
    keyword3: "ferramenta de bibliografia anotada",
    ogTitle: "Estúdio de bibliografia",
    ogDescription:
      "Um espaço privado em página inteira para organizar, formatar, anotar, importar e exportar citações.",
    ogImageTitle: "Estúdio de bibliografia",
    ogImageSubtitle:
      "Organize, anote, reformate, exporte e compartilhe suas referências.",
    ogKind: "Espaço de citações",
    breadcrumb: "Estúdio de bibliografia",
    eyebrow: "Espaço privado no navegador",
    lead: "Gerencie todas as referências de um trabalho em um só lugar. Seus projetos ficam neste navegador, a menos que você decida exportá-los ou compartilhá-los.",
    privacyTitle: "Privado por padrão",
    privacyBody:
      "Projetos e anotações usam o armazenamento local do navegador. Links compartilhados incluem citações, mas omitem intencionalmente notas privadas.",
    workflowTitle: "Da lista de fontes ao trabalho final",
    workflowBody:
      "Alterne projetos, encontre e ordene referências, adicione anotações ou localizadores, corrija metadados e reformate toda a lista em um único estilo.",
    formatsTitle: "Importe referências e leve-as para qualquer lugar",
    formatsBody:
      "Importe BibTeX, RIS ou CSL-JSON. Copie listas formatadas ou exporte Word, RTF, RIS, CSL-JSON, HTML, BibTeX e versões anotadas.",
  },

  // The 95-term glossary: /glossary and /glossary/[slug].
  //
  // The chrome only. Each term's own prose — `short`, the `body` paragraphs, the
  // example — is ~10,600 words that cannot live in a flat string map and must not be
  // copied verbatim into sixteen files; it lives in `src/content/glossary/<locale>.json`
  // and is read through `lib/localized-content.ts`.
  glossaryPage: {
    metaTitle: "{term} - definição e exemplos",
    notFoundTitle: "Termo não encontrado",
    keywordDefinition: "definição de {term}",
    keywordWhatIs: "o que é {term}",
    ogTitle: "{term} - glossário do CitationEasy",
    ogKind: "Glossário de citação",
    breadcrumb: "Glossário",
    alsoCalled: "Também chamado de: {aliases}",
    doItHeading: "Faça isso no CitationEasy",
    seeAlsoHeading: "Veja também",
    moreInHeading: "Mais em {category}",
    navLabel: "Navegação do glossário",
    allTermsLink: "Os {count} termos do glossário",
    generatorLink: "Gerador de citações gratuito",
    termSetName: "Glossário de citação do CitationEasy",
    hubMetaTitle: "Glossário de citação - {count} termos explicados",
    hubMetaDescription:
      "Definições claras de {count} termos de citação e escrita acadêmica: et al., ibid., DOI, recuo deslocado, patchwriting, versão de registro e mais — cada um com exemplos.",
    hubKeyword1: "glossário de citação",
    hubKeyword2: "termos de citação",
    hubKeyword3: "glossário de escrita acadêmica",
    hubKeyword4: "o que significa et al",
    hubKeyword5: "o que é um DOI",
    hubKeyword6: "terminologia de referenciação",
    hubOgTitle: "Glossário de citação",
    hubOgDescription:
      "Definições de {count} termos de citação e escrita acadêmica, com exemplos.",
    hubOgImageTitle: "Glossário de citação",
    hubOgImageSubtitle:
      "{count} termos de citação e escrita acadêmica, explicados de forma clara e simples com exemplos.",
    hubOgKind: "Referência",
    hubTermSetDescription:
      "Definições de {count} termos de citação, referenciação e escrita acadêmica.",
    hubCollectionName: "Glossário de citação",
    hubCollectionDescription:
      "Definições claras de {count} termos de citação e escrita acadêmica.",
    hubHeading: "Glossário de citação",
    hubIntro:
      "{count} termos de citação e escrita acadêmica, explicados de forma clara e simples com exemplos — as abreviações que aparecem nos guias de estilo, as partes que compõem uma referência, os identificadores que localizam uma fonte e o vocabulário da publicação acadêmica.",
    hubJumpLabel: "Ir para a letra",
    hubBrowseHeading: "Explorar por tema",
    hubAllHeading: "Todos os termos de A a Z",
    hubWorkHeading: "Coloque os termos em prática",
    hubWorkGenerator: "Gerador de citações gratuito",
    hubWorkStyles: "Guias de estilos de citação",
    hubWorkHowTo: "Como citar qualquer fonte",
    hubWorkCompare: "Comparar estilos de citação",
    hubWorkTools: "Ferramentas de escrita e citação",
    hubWorkFaq: "Perguntas frequentes sobre citação",
  },

  // /faq — the 17-question hub. The questions and answers themselves are in
  // `src/data/faq.ts` (the one source the API, the MCP tool and /md also read); this is
  // the page around them.
  faqPage: {
    metaTitle:
      "Perguntas frequentes sobre citação - Respostas rápidas às dúvidas mais comuns",
    metaDescription:
      "Respostas rápidas e precisas às perguntas sobre citação que os estudantes realmente fazem: DOIs, fontes sem autor, et al., recuo deslocado, como citar IA, regras sobre plágio, APA versus MLA e mais — com links para os guias completos.",
    ogTitle: "Perguntas frequentes sobre citação",
    ogDescription:
      "Respostas rápidas às perguntas mais comuns sobre citação, dos DOIs e et al. a como citar o ChatGPT.",
    ogImageTitle: "Perguntas e respostas sobre citação",
    ogImageSubtitle:
      "Respostas curtas e precisas às perguntas que os estudantes realmente fazem sobre citação.",
    ogKind: "Perguntas frequentes",
    breadcrumbLabel: "Trilha de navegação",
    breadcrumbHome: "Início",
    breadcrumbCurrent: "Perguntas frequentes sobre citação",
    heading: "Perguntas frequentes sobre citação",
    intro:
      "Respostas rápidas e precisas às perguntas sobre citação que os estudantes realmente fazem — com links para o guia ou a ferramenta completa quando você precisar de mais profundidade.",
    contentsLabel: "Conteúdo das perguntas frequentes",
    stillStuckHeading: "Ainda com dúvidas?",
    stillStuckBody:
      "Explore a [biblioteca completa de guias](/guides), consulte os [guias rápidos por estilo](/cheat-sheets), procure um termo no [glossário de citação](/glossary), [pesquise em todo o site](/search) ou simplesmente [gere a citação](/) e deixe o formatador aplicar as regras para você.",
  },

  // /cheat-sheets and its three style sheets. The citation FORMATS in them
  // ("Author, A. A. (Year)…") are the styles' own templates and stay as they are in
  // every language, exactly as the per-style examples do; the labels around them do not.
  cheatSheets: {
    hubMetaTitle:
      "Guias rápidos de estilos de citação - Guias de referência rápida",
    hubMetaDescription:
      "Guias rápidos para os estilos de citação APA, MLA e Chicago. Guias prontos para impressão com exemplos dos tipos de fonte mais comuns.",
    hubBreadcrumbHome: "Início",
    hubBreadcrumbCurrent: "Guias rápidos de estilos de citação",
    hubBackLink: "← Voltar ao gerador",
    hubHeading: "Guias rápidos de estilos de citação",
    hubIntro:
      "Guias de referência rápida para os estilos de citação mais populares. Prontos para impressão e sempre atualizados.",
    hubApaTitle: "Guia rápido de APA 7ª edição",
    hubApaDesc: "Psicologia, ciências sociais, educação, enfermagem",
    hubMlaTitle: "Guia rápido de MLA 9ª edição",
    hubMlaDesc: "Literatura, línguas, humanidades, estudos culturais",
    hubChicagoTitle: "Guia rápido do estilo Chicago",
    hubChicagoDesc: "História, história da arte, filosofia, religião",
    backToSheets: "← Voltar aos guias rápidos",
    generalRules: "Regras gerais",
    inTextCitations: "Citações no texto",
    colSituation: "Situação",
    colFormat: "Formato",
    colExample: "Exemplo",
    book: "Livro",
    journalArticle: "Artigo de periódico",
    website: "Site",
    apaMetaTitle: "Guia rápido de APA 7ª edição - Guia de referência rápida",
    apaMetaDescription:
      "Guia rápido de citações em APA 7ª edição com exemplos para livros, periódicos, sites e mais. Referência rápida pronta para impressão.",
    apaHeading: "Guia rápido de APA 7ª edição",
    apaLead:
      "Referência rápida para os formatos de citação em APA mais comuns.",
    apaRule1:
      'Use **recuo deslocado** (primeira linha alinhada à esquerda, linhas seguintes com recuo de 1,27 cm/0,5")',
    apaRule2: "Use espaçamento duplo em todas as entradas",
    apaRule3: "Organize em ordem alfabética pelo sobrenome do autor",
    apaRule4: "Use itálico nos títulos de livros, periódicos e volumes",
    apaRule5: "Use o DOI quando disponível",
    apaRow1Situation: "1 autor",
    apaRow1Format: "(Author, Year)",
    apaRow1Example: "(Smith, 2024)",
    apaRow2Situation: "2 autores",
    apaRow2Format: "(Author & Author, Year)",
    apaRow2Example: "(Smith & Jones, 2024)",
    apaRow3Situation: "3 ou mais autores",
    apaRow3Format: "(First Author et al., Year)",
    apaRow3Example: "(Smith et al., 2024)",
    apaRow4Situation: "Citação direta",
    apaRow4Format: "(Author, Year, p. #)",
    apaRow4Example: "(Smith, 2024, p. 45)",
    apaRow5Situation: "Sem autor",
    apaRow5Format: '("Title," Year)',
    apaRow5Example: '("Study Results," 2024)',
    apaReferenceHeading: "Exemplos da lista de referências",
    apaBookExample:
      "Author, A. A. (Year). *Title of work: Capital letter also for subtitle*. Publisher. https://doi.org/xxxxx",
    apaJournalExample:
      "Author, A. A., & Author, B. B. (Year). Title of article. *Title of Periodical, Volume*(Issue), Page–Page. https://doi.org/xxxxx",
    apaWebsiteExample:
      "Author, A. A. (Year, Month Day). *Title of page*. Site Name. https://www.example.com/page",
    apaChapterLabel: "Capítulo de livro organizado",
    apaChapterExample:
      "Author, A. A. (Year). Title of chapter. In E. E. Editor (Ed.), *Title of book* (pp. #–#). Publisher.",
    apaConferenceLabel: "Artigo de congresso",
    apaConferenceExample:
      "Author, A. A. (Year, Month Days). *Title of paper* [Paper presentation]. Conference Name, Location.",
    apaDifferencesHeading: "Principais diferenças em relação à APA 6ª edição",
    apaDifference1: 'Não se usa mais "Retrieved from" antes das URLs',
    apaDifference2: "Inclua o DOI no formato de link https://doi.org/",
    apaDifference3: "Liste até 20 autores antes de usar reticências",
    apaDifference4: "A localização da editora não é mais exigida",
    apaDifference5:
      'Use "they" no singular como pronome neutro em gênero (norma da APA para o inglês)',
    apaCtaText: "Gere citações perfeitas em APA automaticamente",
    apaCtaButton: "Gerador de citações APA →",
    mlaMetaTitle: "Guia rápido de MLA 9ª edição - Guia de referência rápida",
    mlaMetaDescription:
      "Guia rápido de citações em MLA 9ª edição com exemplos para livros, artigos, sites e mais. Referência rápida pronta para impressão.",
    mlaHeading: "Guia rápido de MLA 9ª edição",
    mlaLead:
      "Referência rápida para os formatos de citação em MLA mais comuns.",
    mlaRule1: "Use **recuo deslocado**",
    mlaRule2: "Use espaçamento duplo em todas as entradas",
    mlaRule3: "Organize em ordem alfabética pelo sobrenome do autor",
    mlaRule4: 'Dê à página o título "Works Cited" (obras citadas)',
    mlaRule5:
      "Use itálico nos títulos de contêineres (livros, periódicos, sites)",
    mlaRow1Situation: "1 autor",
    mlaRow1Format: "(Author Page)",
    mlaRow1Example: "(Smith 45)",
    mlaRow2Situation: "2 autores",
    mlaRow2Format: "(Author and Author Page)",
    mlaRow2Example: "(Smith and Jones 45)",
    mlaRow3Situation: "3 ou mais autores",
    mlaRow3Format: "(First Author et al. Page)",
    mlaRow3Example: "(Smith et al. 45)",
    mlaRow4Situation: "Sem autor",
    mlaRow4Format: '("Title" Page)',
    mlaRow4Example: '("Study Results" 45)',
    mlaRow5Situation: "Sem número de página",
    mlaRow5Format: "(Author)",
    mlaRow5Example: "(Smith)",
    mlaWorksCitedHeading: "Exemplos de obras citadas",
    mlaBookExample: "Last, First. *Title of Book*. Publisher, Year.",
    mlaJournalExample:
      'Last, First. "Title of Article." *Journal Title*, vol. #, no. #, Year, pp. #–#.',
    mlaWebsiteExample:
      'Last, First. "Title of Page." *Website Name*, Day Month Year, URL.',
    mlaAnthologyLabel: "Antologia/Livro organizado",
    mlaAnthologyExample:
      'Last, First. "Title of Essay." *Title of Collection*, edited by First Last, Publisher, Year, pp. #–#.',
    mlaCoreHeading: "Elementos essenciais do MLA (em ordem)",
    mlaCore1: "Autor.",
    mlaCore2: '"Título da fonte."',
    mlaCore3: "*Título do contêiner*,",
    mlaCore4: "Colaboradores,",
    mlaCore5: "Versão,",
    mlaCore6: "Número,",
    mlaCore7: "Editora,",
    mlaCore8: "Data de publicação,",
    mlaCore9: "Localização (páginas, URL, DOI).",
    mlaCtaText: "Gere citações perfeitas em MLA automaticamente",
    mlaCtaButton: "Gerador de citações MLA →",
    chicagoMetaTitle:
      "Guia rápido do estilo Chicago - Guia de referência rápida",
    chicagoMetaDescription:
      "Guia rápido de citações do Chicago Manual of Style com exemplos dos sistemas notas-bibliografia e autor-data. Referência rápida pronta para impressão.",
    chicagoHeading: "Guia rápido do estilo Chicago",
    chicagoLead:
      "Referência rápida para os formatos de citação do Chicago Manual of Style (18ª edição).",
    chicagoSystemsHeading: "Os dois sistemas Chicago",
    chicagoColNb: "Notas-Bibliografia (NB)",
    chicagoColAd: "Autor-Data (AD)",
    chicagoRow1Nb: "Notas de rodapé/notas finais + bibliografia",
    chicagoRow1Ad: "Citações entre parênteses + lista de referências",
    chicagoRow2Nb: "História, artes, humanidades",
    chicagoRow2Ad: "Ciências, ciências sociais",
    chicagoRow3Nb: "Primeiras notas mais detalhadas",
    chicagoRow3Ad: "Semelhante ao formato APA",
    chicagoExamplesHeading: "Exemplos de notas-bibliografia",
    chicagoNoteLabel: "Nota:",
    chicagoBibliographyLabel: "Bibliografia:",
    chicagoBookLabel: "Livro (nota de rodapé)",
    chicagoBookNote: "1. First Last, *Title of Book* (Publisher, Year), page.",
    chicagoBookBibliography: "Last, First. *Title of Book*. Publisher, Year.",
    chicagoJournalLabel: "Artigo de periódico (nota de rodapé)",
    chicagoJournalNote:
      '1. First Last, "Title of Article," *Journal Title* Vol, no. Issue (Year): pages.',
    chicagoJournalBibliography:
      'Last, First. "Title of Article." *Journal Title* Vol, no. Issue (Year): pages.',
    chicagoWebsiteLabel: "Site (nota de rodapé)",
    chicagoWebsiteNote:
      '1. First Last, "Title of Page," Site Name, Month Day, Year, URL.',
    chicagoWebsiteBibliography:
      'Last, First. "Title of Page." Site Name. Month Day, Year. URL.',
    chicagoShortHeading: "Notas abreviadas",
    chicagoShortIntro:
      "Depois da primeira citação completa, use notas de rodapé abreviadas:",
    chicagoShortExample: "1. Last, *Short Title*, page.",
    chicagoRulesHeading: "Principais regras de formatação",
    chicagoRule1:
      "Use números sobrescritos para as referências de notas de rodapé no texto",
    chicagoRule2: "As entradas da bibliografia usam recuo deslocado",
    chicagoRule3:
      'Use uma nota abreviada em vez de "Ibid." para citações consecutivas da mesma fonte',
    chicagoRule4:
      "Títulos: livros e periódicos em itálico; artigos e capítulos entre aspas",
    chicagoRule5:
      "Indique as datas de acesso para fontes on-line que possam mudar",
    chicagoCtaText: "Gere citações perfeitas em Chicago automaticamente",
    chicagoCtaButton: "Gerador de citações Chicago →",
  },

  // The catalog's page-type labels (`CATALOG_TYPE_LABELS`), which the search page shows
  // as facet chips and beside every result. Layer 2: a fact from `src/lib/**` rather
  // than page chrome, and English until it is mapped through here.
  catalogTypes: {
    core: "Páginas principais",
    styleLanding: "Centrais dos principais estilos de citação",
    style: "Páginas de referência de estilos de citação",
    styleExamples: "Páginas de exemplos de citação",
    styleFormat: "Guias de formatação de trabalhos",
    styleInText: "Guias de citação no texto",
    styleAnnotatedBibliography: "Guias de bibliografia anotada",
    styleReferencePage: "Guias da lista de referências/obras citadas",
    tool: "Ferramentas gratuitas de escrita e citação",
    guide: "Guias educativos",
    guideCategory: "Categorias de guias",
    toolCollection: "Coleções de ferramentas",
    howToCite: "Como citar <fonte> em <estilo>",
    comparison: "Comparações de estilos de citação",
    converter: "Conversores de identificadores e formatos",
    field: "Estilos de citação por área acadêmica",
    region: "Estilos de citação por país/região",
    category: "Estilos de citação por categoria temática",
    cheatSheet: "Guias rápidos de uma página",
    glossary: "Glossário de citação e escrita acadêmica",
  },

  // /about
  aboutPage: {
    metaTitle: "Sobre o CitationEasy - Gerador de citações gratuito",
    metaDescription:
      "O CitationEasy é um gerador de citações gratuito que aceita {count} estilos. Criado por Samuel Sergi com ferramentas de código aberto como Citation Style Language, Citation.js e Next.js.",
    backLink: "← Voltar ao gerador",
    heading: "Sobre o CitationEasy",
    intro:
      "O CitationEasy é um gerador de citações gratuito e de acesso aberto, criado para estudantes, pesquisadores e redatores acadêmicos. Ele aceita **{count} estilos de citação** e oferece uma coleção cada vez maior de ferramentas de escrita, guias educativos e recursos de referência — tudo sem necessidade de cadastro ou assinatura.",
    offerHeading: "O que oferecemos",
    offer1:
      "[Gerador de citações](/) com {count} estilos, incluindo APA 7ª ed., MLA 9ª ed., Chicago 18ª ed., Harvard, Vancouver, IEEE e milhares de outros",
    offer2:
      "[40 ferramentas gratuitas de escrita e citação](/tools) — contador de palavras, verificador de legibilidade, validador de DOI, organizador alfabético de bibliografia e mais",
    offer3:
      "[750 guias de como citar](/how-to-cite) cobrindo todos os principais tipos de fonte em 6 estilos de citação",
    offer4:
      "[Mais de 100 guias educativos](/guides) sobre boas práticas de citação, escrita acadêmica e habilidades de pesquisa",
    offer5:
      "[Ferramenta de comparação de estilos](/compare), [quiz de estilos de citação](/quiz) e [guias rápidos](/cheat-sheets) para consulta rápida",
    offer6:
      "7 formatos de exportação: texto simples, BibTeX, Word, lista numerada, lista com marcadores, RIS e CSV",
    openSourceHeading: "Feito com código aberto",
    openSourceIntro:
      "O CitationEasy é possível graças a vários excelentes projetos e APIs de código aberto. Acreditamos em dar crédito a quem merece:",
    openSource1:
      "[Citation Style Language (CSL)](https://citationstyles.org/) — o padrão aberto que move nossos {count} estilos de citação. O CSL é mantido por uma comunidade de voluntários e usado por gerenciadores de referências como Zotero, Mendeley e Papers. As definições dos estilos vêm do [repositório de estilos CSL](https://github.com/citation-style-language/styles) no GitHub.",
    openSource2:
      "[Citation.js](https://citation.js.org/) — a biblioteca JavaScript que processa citações e as formata de acordo com os estilos CSL. Criada por Lars Willighagen.",
    openSource3:
      "[Open Library API](https://openlibrary.org/developers/api) — a API gratuita de busca de livros fornecida pelo Internet Archive, usada para buscar metadados de livros por título ou ISBN.",
    openSource4:
      "[DOI Foundation](https://www.doi.org/) — o sistema de Identificador de Objeto Digital (DOI) usado para resolver e validar DOIs.",
    createdHeading: "Criado por",
    createdBody:
      "O CitationEasy foi criado e é mantido por **Samuel Sergi**. O objetivo é simples: oferecer uma ferramenta de citação genuinamente gratuita e de alta qualidade, sem recursos bloqueados por paywalls nem exigência de conta.",
    contactPrompt: "Tem perguntas, feedback ou sugestões? Entre em contato:",
    contactEmail: "E-mail: [sam@sammysergi.com](mailto:sam@sammysergi.com)",
    missionHeading: "Nossa missão",
    mission1:
      "Citações precisas não deveriam custar dinheiro. Muitas ferramentas de citação se anunciam como gratuitas, mas restringem os formatos de exportação, limitam o número de citações ou empurram os usuários para planos premium. O CitationEasy tem o compromisso de ser completamente gratuito — sem cadastro, sem limites, sem nível premium.",
    mission2:
      "Todos os recursos deste site, do gerador de citações às ferramentas de escrita e aos guias educativos, estão disponíveis a todos sem custo algum. Acreditamos que as ferramentas de integridade acadêmica devem ser acessíveis a todos os estudantes e pesquisadores, independentemente do orçamento.",
  },

  // /contact
  contactPage: {
    metaTitle: "Contato",
    metaDescription:
      "Entre em contato com o CitationEasy. Tem perguntas, feedback ou sugestões sobre nosso gerador de citações e ferramentas de escrita gratuitos? Adoraríamos ouvir você.",
    backLink: "← Voltar ao gerador",
    heading: "Contato",
    intro:
      "Tem perguntas, feedback ou sugestões sobre o CitationEasy? Adoraríamos ouvir você. Seja para relatar um erro, pedir um novo recurso ou pedir ajuda com uma citação, não hesite em entrar em contato.",
    touchHeading: "Entre em contato",
    touchBody:
      "A melhor forma de nos contatar é por e-mail. Nosso objetivo é responder em até 48 horas.",
    emailLine: "**E-mail:** [sam@sammysergi.com](mailto:sam@sammysergi.com)",
    commonHeading: "Perguntas comuns",
    commonIntro:
      "Antes de entrar em contato, você pode encontrar a resposta para sua pergunta em um destes recursos:",
    resource1:
      "[Guias de citação](/guides) — instruções passo a passo para citar fontes em todos os principais estilos",
    resource2:
      "[Guias de como citar](/how-to-cite) — 750 combinações de tipo de fonte e estilo, com exemplos",
    resource3:
      "[Guias rápidos](/cheat-sheets) — referência rápida de formatação APA, MLA e Chicago",
    resource4:
      "[Ferramentas de escrita](/tools) — contador de palavras, verificador de legibilidade, validador de DOI e mais",
    issueHeading: "Relatar um problema",
    issueIntro:
      "Se você encontrou um erro de formatação em uma citação, um recurso quebrado ou um estilo incorreto, inclua o máximo de detalhes possível ao entrar em contato:",
    issue1: "O estilo de citação que você estava usando",
    issue2: "O tipo de fonte (livro, site, artigo de periódico etc.)",
    issue3: "O que você esperava versus o que foi gerado",
    issue4: "Seu navegador e dispositivo (se relevante)",
    issueOutro:
      "Isso nos ajuda a identificar e corrigir o problema rapidamente.",
    aboutHeading: "Sobre o CitationEasy",
    aboutBody:
      "O CitationEasy é um gerador de citações gratuito que aceita mais de 10.000 estilos de citação. Foi criado por Samuel Sergi com o objetivo de tornar as citações precisas acessíveis a todos. Saiba mais em nossa [página Sobre](/about).",
  },

  // /ai — the agent and developer docs.
  //
  // The code samples, endpoint paths and MCP tool NAMES are deliberately absent: they are
  // the API's identifiers, served in English by the API itself, and a translated one
  // would document an endpoint that does not exist.
  aiPage: {
    metaTitle:
      "CitationEasy para assistentes e agentes de IA - API, servidor MCP, llms.txt",
    metaDescription:
      "Use o CitationEasy de forma programática: uma API JSON gratuita e sem autenticação para citações em {count} estilos, um servidor MCP para agentes, llms.txt, uma especificação OpenAPI e visualizações em markdown de todas as páginas.",
    keyword1: "API de citação",
    keyword2: "servidor MCP",
    keyword3: "API de gerador de citações",
    keyword4: "llms.txt",
    keyword5: "OpenAPI",
    keyword6: "citações para agentes de IA",
    keyword7: "API de citação gratuita",
    webApiDescription:
      "API JSON gratuita e sem autenticação que formata citações em {count} estilos de Citation Style Language a partir de uma URL, DOI, ISBN, PubMed ID, arXiv ID ou metadados CSL-JSON.",
    breadcrumb: "Para IA e agentes",
    heading: "CitationEasy para assistentes e agentes de IA",
    intro:
      "Tudo neste site está disponível tanto para software quanto para pessoas: uma API JSON gratuita, um servidor MCP, uma especificação OpenAPI, um índice llms.txt e uma visualização em markdown de todas as {count} páginas. Sem chave, sem conta, sem limite de requisições próprio.",
    whyHeading: "Por que chamar a API em vez de escrever a citação você mesmo",
    whyBody:
      "Formatar uma citação parece um problema de geração de texto, mas não é. Cada estilo codifica centenas de regras sobre inversão de autores, iniciais, capitalização de títulos, pontuação de contêineres, indicações de edição e posicionamento de datas, e elas diferem de formas fáceis de errar sutilmente de memória. O CitationEasy executa o **citeproc-js** sobre as definições oficiais da Citation Style Language — o mesmo mecanismo usado por Zotero e Mendeley —, então o resultado é determinístico e corresponde ao que o sistema de submissão de um periódico espera. Os metadados vêm do Crossref, Open Library e PubMed; nada é inventado, e um campo ausente na origem permanece ausente em vez de ser preenchido com um palpite plausível.",
    generateHeading: "Gerar uma citação",
    generateBody:
      "Envie `inputs` em vez de `input` para até 10 fontes em uma única chamada, ou `entry` com metadados CSL-JSON que você já tenha para pular a busca por completo. Adicione `note: true` para obter a forma de nota de rodapé. As falhas por fonte retornam associadas a essa fonte, então um lote nunca falha por completo.",
    mcpHeading: "Conecte-se via MCP",
    mcpIntro:
      "O endpoint do Model Context Protocol em `/api/mcp` fala JSON-RPC 2.0 sobre HTTP em streaming e não precisa de credenciais.",
    mcpToolsHeading: "Ferramentas que ele expõe",
    endpointsHeading: "Endpoints",
    colEndpoint: "Endpoint",
    colWhat: "O que faz",
    endpointCite:
      "Formata até 10 fontes de uma vez. Aceita uma URL, DOI, ISBN, PubMed ID, arXiv ID, texto BibTeX/RIS/CSL-JSON ou um objeto de entrada CSL-JSON. Retorna a entrada de referência e, opcionalmente, a citação no texto e a nota de rodapé.",
    endpointStyles:
      "Busca entre os {count} estilos por nome, classe de formato de citação, área ou região.",
    endpointStyleSlug:
      "Um estilo completo, com exemplos reais formatados produzidos por sua própria definição CSL.",
    endpointSearch:
      "Busca ordenada por relevância em todas as páginas do site.",
    endpointPages:
      "O catálogo completo do site como entradas estruturadas, filtrável por tipo de página.",
    endpointHowToCite:
      "Todas as páginas de orientação de tipo de fonte × estilo, filtráveis por qualquer um dos dois.",
    endpointTools:
      "Todas as ferramentas gratuitas, com sua categoria e palavras-chave.",
    endpointGuides: "Todos os guias educativos, com sua categoria.",
    endpointConverters:
      "Todas as conversões de identificadores e formatos que o site oferece.",
    endpointComparisons:
      "Comparações de estilos lado a lado, vinculadas aos registros de ambos os estilos.",
    endpointFields:
      'Áreas acadêmicas, categorias temáticas e regiões — para responder "qual estilo devo usar?".',
    endpointFaq:
      "Respostas verificadas a perguntas comuns sobre citação, filtráveis com q.",
    endpointGlossary:
      "Termos de citação e escrita acadêmica com suas definições completas — a resposta traz o texto, então não há página para buscar depois.",
    endpointIndex:
      "Documento de descoberta de endpoints. Comece aqui se estiver explorando.",
    discoveryHeading: "Arquivos de descoberta",
    discoveryLlms:
      "Índice selecionado do site escrito para modelos de linguagem.",
    discoveryLlmsFull:
      "Todas as páginas que o site publica, agrupadas por tipo, uma linha cada.",
    discoveryOpenapi: "Descrição OpenAPI 3.1 de toda a API pública.",
    discoveryMcp:
      "Endpoint do MCP (HTTP em streaming). Faça um GET em um navegador para ver uma descrição.",
    discoveryWellKnownMcp:
      "Documento de descoberta do MCP para agentes que sondam um domínio.",
    discoveryAiPlugin:
      "Manifesto de plugin legado, para frameworks que ainda procuram um.",
    discoveryMd: "Visualização em markdown de qualquer página do site.",
    discoverySitemap: "Todas as URLs indexáveis.",
    catalogHeading: "O que há no catálogo",
    catalogIntro:
      "Cada página abaixo pode ser acessada como HTML, como markdown em `/md/` e como entrada estruturada em `/api/v1/pages`.",
    catalogStyles:
      "**{count}** estilos de citação, cada um com exemplos reais gerados a partir de sua própria definição CSL",
    catalogTools: "**{count}** ferramentas gratuitas de navegador",
    catalogGuides: "**{count}** guias educativos",
    catalogHowToCite: "**{count}** páginas de como citar",
    catalogComparisons: "**{count}** comparações de estilos",
    catalogConverters: "**{count}** conversores",
    catalogFields: "**{count}** áreas acadêmicas",
    catalogStyleLandings:
      "**{count}** centrais de estilos principais com regras de formatação completas",
    faqHeading: "Perguntas frequentes",
    faqFreeQ:
      "A API do CitationEasy é gratuita, e preciso de uma chave de API?",
    faqFreeA:
      "Ela é gratuita e não há chave de API. Todo endpoint em /api/v1 não exige autenticação e tem CORS aberto, então você pode chamá-lo de um navegador, um notebook ou um agente sem se cadastrar. Não há limite de requisições próprio, mas as buscas de identificadores passam pelo Crossref, Open Library e PubMed, que aplicam seus próprios limites de uso justo — agrupe requisições com `inputs` em vez de disparar muitas requisições em paralelo.",
    faqLlmQ: "As citações são geradas por um modelo de linguagem?",
    faqLlmA:
      "Não. A formatação executa o citeproc-js sobre as definições oficiais da Citation Style Language, o mesmo mecanismo usado por gerenciadores de referências. A mesma entrada e o mesmo estilo sempre produzem a mesma string. Os metadados bibliográficos vêm do Crossref (DOIs), Open Library (ISBNs) e PubMed (PMIDs) — o CitationEasy não inventa campos, então qualquer coisa ausente na origem fica ausente na saída em vez de ser adivinhada.",
    faqMcpQ: "Como conecto um agente ao CitationEasy via MCP?",
    faqMcpA:
      "Aponte qualquer cliente do Model Context Protocol para https://www.citationeasy.com/api/mcp usando o transporte HTTP em streaming. Nenhuma credencial é necessária. No Claude Code: `claude mcp add --transport http citationeasy https://www.citationeasy.com/api/mcp`. O servidor expõe ferramentas para gerar citações, buscar entre os {count} estilos, orientação por tipo de fonte, busca no site e leitura de qualquer página como markdown.",
    faqStylesQ: "Quais estilos de citação a API pode gerar?",
    faqStylesA:
      'Todos os {count} estilos do repositório da Citation Style Language, incluindo APA 7, MLA 9, os dois sistemas Chicago, Harvard, Vancouver, IEEE, AMA e milhares de estilos individuais de periódicos. Passe o slug do estilo no campo `style`. Slugs amigáveis como "chicago" e "harvard" resolvem para a variante CSL correta, e /api/v1/styles?q= encontra o restante.',
    faqLlmsTxtQ: "O que é o llms.txt e onde está o do CitationEasy?",
    faqLlmsTxtA:
      "llms.txt é um índice em texto simples de um site, escrito para modelos de linguagem em vez de navegadores. O do CitationEasy está em /llms.txt; a versão exaustiva que lista todas as páginas do site está em /llms-full.txt.",
    faqMarkdownQ:
      "Posso ler as páginas do CitationEasy como markdown em vez de HTML?",
    faqMarkdownA:
      "Sim. Coloque /md/ antes de qualquer caminho do site — por exemplo, /md/citation-style/apa ou /md/tools/word-counter. As páginas baseadas em dados retornam seu conteúdo completo, incluindo os exemplos reais gerados a partir da própria definição CSL de cada estilo. Páginas de texto escritas à mão (guias, guias rápidos) retornam um cartão-resumo e a URL HTML canônica, já que o texto delas vive na própria página.",
    faqReuseQ: "Posso usar a saída do CitationEasy no meu próprio produto?",
    faqReuseA:
      "Sim. Uma citação formatada é um fato sobre uma fonte; use-a livremente. As próprias definições de estilo CSL vêm do projeto Citation Style Language sob a licença CC BY-SA 3.0. A atribuição ao CitationEasy é bem-vinda, mas não é obrigatória.",
    missingHeading: "Falta alguma coisa?",
    missingBody:
      "Se seu agente precisa de um endpoint, um campo ou um formato que não está aqui, [conte para nós](/contact). Prefere explorar manualmente? [Comece pelo gerador de citações](/).",
  },

  // /quiz. The client component reads these as a prop rather than through a context:
  // it is one component, not the generator's nine, so there is nothing to drill through.
  quizPage: {
    metaTitle: "Qual estilo de citação devo usar? - Quiz gratuito de estilos",
    metaDescription:
      "Responda a algumas perguntas rápidas sobre sua área, o tipo de trabalho e suas preferências para encontrar o melhor estilo de citação para o seu trabalho. APA, MLA, Chicago, Vancouver e mais.",
    ogTitle: "Qual estilo de citação devo usar?",
    ogDescription:
      "Faça nosso quiz gratuito para encontrar o melhor estilo de citação para o seu trabalho.",
    breadcrumbHome: "CitationEasy",
    breadcrumbCurrent: "Quiz de estilos",
    backLink: "← Voltar ao gerador",
    heading: "Qual estilo de citação devo usar?",
    intro:
      "Responda a algumas perguntas rápidas e recomendaremos o melhor estilo de citação para o seu trabalho.",
    progress: "Pergunta {current} de {total}",
    qDiscipline: "Qual é a sua área de estudo?",
    optSocialScience: "Psicologia / Ciências sociais",
    optHumanities: "Literatura / Humanidades",
    optHistory: "História / História da arte",
    optMedical: "Medicina / Enfermagem / Saúde",
    optEngineering: "Engenharia / Ciência da computação",
    optNaturalScience: "Ciências naturais (biologia, química)",
    optLaw: "Direito",
    optBusiness: "Administração / Economia",
    qPaperType: "Que tipo de trabalho você está escrevendo?",
    optResearch: "Trabalho de pesquisa ou tese",
    optReview: "Revisão de literatura",
    optReport: "Relatório de laboratório ou relatório técnico",
    optEssay: "Redação ou tarefa de aula",
    optJournal: "Submissão de artigo a periódico",
    qRegion: "Onde fica a sua instituição?",
    optUs: "Estados Unidos",
    optUk: "Reino Unido / Austrália",
    optCanada: "Canadá",
    optEurope: "Europa (fora do Reino Unido)",
    optOther: "Outro",
    qPreference: "Você tem preferência por algum formato de citação?",
    optAuthorDate: "Citações no texto (Autor, Ano)",
    optNumeric: "Referências numeradas [1]",
    optFootnotes: "Notas de rodapé",
    optNoPreference: "Sem preferência",
    styleVancouver: "Vancouver",
    styleAma: "AMA (American Medical Association)",
    styleIeee: "IEEE",
    styleBluebook: "Bluebook",
    styleCse: "CSE (Council of Science Editors)",
    styleChicagoNotes: "Chicago (notas e bibliografia)",
    styleMla: "MLA (9ª edição)",
    styleApa: "APA (7ª edição)",
    styleHarvard: "Harvard",
    reasonVancouverMedical:
      "Vancouver é o padrão em periódicos médicos e de ciências da saúde, usando referências numeradas.",
    reasonAma:
      "AMA é amplamente usado em periódicos médicos e utiliza citações numeradas.",
    reasonIeee:
      "IEEE é o estilo de citação padrão em engenharia, ciência da computação e áreas de TI.",
    reasonBluebook:
      "O Bluebook é o estilo de citação definitivo para a redação jurídica. O estilo Chicago é a alternativa de uso geral mais próxima.",
    reasonCse: "CSE é o padrão em biologia e ciências naturais.",
    reasonChicagoHistory:
      "Chicago (notas e bibliografia) é o padrão-ouro em história e história da arte, usando notas de rodapé para as citações.",
    reasonChicagoHumanities:
      "Chicago (notas e bibliografia) funciona bem em humanidades graças à sua abordagem baseada em notas de rodapé.",
    reasonMla: "MLA é o padrão em literatura, línguas e humanidades.",
    reasonApaBusiness:
      "APA é amplamente usado nas áreas de administração, economia e gestão.",
    reasonHarvard:
      "O sistema Harvard é o estilo mais comum nas universidades do Reino Unido e da Austrália.",
    reasonChicagoFootnotes:
      "Chicago (notas e bibliografia) usa notas de rodapé, ideal para citações detalhadas.",
    reasonVancouverNumeric:
      "Vancouver usa citações numeradas, mantendo seu texto limpo e legível.",
    reasonApaDefault:
      "APA é o estilo de citação mais usado nas ciências sociais, educação e muitas outras áreas.",
    recommendLabel: "Recomendamos",
    generateButton: "Gerar citações em {style}",
    retakeButton: "Refazer o quiz",
    guidesLink: "Explore nossos guias de citação para mais ajuda →",
    previousButton: "← Pergunta anterior",
  },

  // /search — the server-rendered site search.
  searchPage: {
    metaTitle: "Buscar no CitationEasy",
    metaDescription:
      "Busque em todas as páginas do CitationEasy de uma vez: {count} estilos de citação, mais de 250 ferramentas gratuitas, guias, páginas de como citar, comparações de estilos, conversores e o glossário.",
    keyword1: "buscar estilos de citação",
    keyword2: "encontrar guia de citação",
    keyword3: "busca de ferramentas de citação",
    ogDescription:
      "Busque em todas as páginas do site de uma vez — estilos, ferramentas, guias, páginas de como citar, comparações, conversores e termos do glossário.",
    ogImageSubtitle:
      "Todos os estilos de citação, ferramentas, guias, páginas de como citar, comparações, conversores e termos do glossário, em uma única busca.",
    ogKind: "Busca",
    breadcrumb: "Busca",
    heading: "Buscar no CitationEasy",
    intro:
      "Todos os estilos de citação, ferramentas, guias, páginas de como citar, comparações, conversores e termos do glossário do site, em uma única busca.",
    inputLabel: "Consulta de busca",
    inputPlaceholder: "ex.: citar um podcast em APA, recuo deslocado, DOI",
    submit: "Buscar",
    browseHeading: "Ou explore por seção",
    browseStyles: "Estilos de citação",
    browseStylesNote: "{count} estilos",
    browseTools: "Ferramentas gratuitas",
    browseToolsNote: "{count} ferramentas",
    browseHowTo: "Como citar qualquer fonte",
    browseHowToNote: "{count} páginas",
    browseGuides: "Guias",
    browseGuidesNote: "{count} guias",
    browseGlossary: "Glossário",
    browseGlossaryNote: "{count} termos",
    browseComparisons: "Comparações de estilos",
    browseComparisonsNote: "{count} comparações",
    browseConverters: "Conversores",
    browseConvertersNote: "{count} conversores",
    browseCheatSheets: "Guias rápidos",
    browseCheatSheetsNote: "{count} guias",
    facetsLabel: "Filtrar por tipo de página",
    facetAll: "Tudo ({count})",
    noMatches: "Nenhuma página corresponde a “{query}”.",
    matchesOne: "{count} página corresponde a “{query}”.",
    matchesMany: "{count} páginas correspondem a “{query}”.",
    pagerLabel: "Páginas de resultados",
    pagerPrevious: "← Anterior",
    pagerNext: "Próxima →",
    pagerPosition: "Página {page} de {total}",
    paletteLoading: "Buscando…",
    paletteError: "A busca não está disponível no momento. Tente novamente.",
    paletteViewAll: "Ver todos os resultados para “{query}”",
    apiNote:
      "Está construindo algo? O mesmo índice está disponível em JSON em [/api/v1/search](/api/v1/search?q=podcast) e via MCP — veja [a documentação para agentes](/ai).",
  },

  // /cite-this-page. The six FAQ answers used to exist twice — once in the layout for the
  // FAQPage markup and once in the page for the visible list — which is exactly the drift
  // this dictionary removes: both now read the same keys.
  citeThisPage: {
    metaTitle:
      "Cite esta página - Citações com um clique (bookmarklet e extensão do Chrome)",
    metaDescription:
      "Cite qualquer página da web com um clique. Arraste o bookmarklet gratuito 'Cite esta página' para sua barra de favoritos, ou instale a extensão gratuita do Chrome com um botão na barra de ferramentas e citação com o botão direito. Funciona também em Chromebooks e navegadores restritos.",
    ogTitle: "Bookmarklet 'Cite esta página' e extensão do Chrome",
    ogDescription:
      "Citações de sites com um clique a partir de qualquer página — um bookmarklet de arrastar e soltar, além de uma extensão gratuita do Chrome com citação pelo botão direito.",
    breadcrumbRoot: "CitationEasy",
    breadcrumbSchemaCurrent: "Bookmarklet Cite esta página",
    appName: "Bookmarklet Cite esta página",
    appDescription:
      "Um bookmarklet gratuito que cita a página da web que você está lendo com um clique — ele abre o CitationEasy com a URL da página já preenchida e formata a citação em APA, MLA, Chicago e mais {count} estilos.",
    breadcrumbLabel: "Trilha de navegação",
    breadcrumbHome: "Início",
    breadcrumbCurrent: "Bookmarklet Cite esta página",
    heading: "Cite esta página: bookmarklet e extensão do Chrome",
    intro:
      "Cite qualquer página da web com um clique — sem cadastro. Arraste o bookmarklet abaixo para sua barra de favoritos (sem instalar nada), ou obtenha a extensão gratuita do Chrome para ter um botão na barra de ferramentas e citação com o botão direito.",
    dragPrompt: "Arraste este botão para sua barra de favoritos:",
    buttonTitle: "Arraste-me para sua barra de favoritos",
    buttonLabel: "📑 Cite esta página",
    dontClick:
      "Não clique aqui — arraste-o primeiro para sua barra de favoritos.",
    installHeading: "Como instalar",
    step1Title: "Exiba sua barra de favoritos",
    step1Body:
      "Na maioria dos navegadores, pressione Ctrl+Shift+B (Cmd+Shift+B no Mac) para exibir ou ocultar a barra de favoritos e ter onde soltar o botão.",
    step2Title: "Arraste o botão para cima",
    step2Body:
      "Clique e segure o botão 'Cite esta página' abaixo, depois arraste-o até sua barra de favoritos e solte. Ele se torna um favorito normal.",
    step3Title: "Clique nele em qualquer página",
    step3Body:
      "Ao ler um artigo, uma notícia ou uma página de periódico, clique no seu novo favorito. O CitationEasy abre com a URL dessa página já preenchida e a citação pronta para copiar.",
    manualHeading: "Não consegue arrastar? Adicione manualmente",
    manualBody:
      'Crie um novo favorito, dê a ele o nome "Cite esta página" e cole isto como a URL/endereço:',
    howHeading: "Como funciona",
    howBody:
      "O bookmarklet não executa nenhum código na página que você está lendo. Ele apenas envia o endereço web dessa página ao CitationEasy, onde nosso servidor busca e analisa os metadados públicos da página — o mesmo caminho confiável usado quando você cola uma URL no gerador manualmente. Isso mantém tudo seguro e evita os limites de origem cruzada que aposentaram os antigos bookmarklets embutidos na página.",
    extensionHeading: "Prefere uma extensão do Chrome?",
    extensionBody:
      "A extensão gratuita do CitationEasy adiciona um botão na barra de ferramentas que cita a aba atual, além de opções no menu de contexto para citar um link — ou um DOI ou ISBN selecionado — sem abri-lo. Assim como o bookmarklet, ela não executa nenhum código nas páginas que você visita: apenas envia a URL ao CitationEasy. Ela precisa apenas das permissões `activeTab` e `contextMenus`, e funciona no Chrome, Edge, Brave e outros navegadores baseados em Chromium.",
    downloadButton: "⬇ Baixar a extensão do Chrome (.zip)",
    install1: "Baixe e descompacte a extensão.",
    install2:
      "Abra `chrome://extensions` e ative o **Modo do desenvolvedor** (canto superior direito).",
    install3:
      "Clique em **Carregar sem compactação** e selecione a pasta descompactada.",
    install4:
      "Fixe o botão do CitationEasy e clique nele em qualquer página para citá-la.",
    faqHeading: "Perguntas frequentes",
    faqWhatQ: "O que é o bookmarklet 'Cite esta página'?",
    faqWhatA:
      "É um pequeno favorito que você arrasta para a barra de favoritos do seu navegador. Ao clicar nele em qualquer página da web, ele abre o CitationEasy em uma nova aba com a URL dessa página já preenchida, então você obtém uma citação formatada com um clique — sem copiar e colar.",
    faqExtensionNeededQ: "Preciso instalar uma extensão?",
    faqExtensionNeededA:
      "Não. Um bookmarklet é apenas um favorito, então não há nada para instalar nem permissões para conceder. Funciona em Chromebooks escolares restritos e na maioria dos navegadores móveis onde as extensões são bloqueadas. Se preferir uma extensão, uma extensão gratuita do Chrome também está disponível nesta página.",
    faqChromeQ: "Existe uma extensão do Chrome?",
    faqChromeA:
      "Sim. A extensão gratuita do Chrome do CitationEasy adiciona um botão na barra de ferramentas que cita a aba atual, além de opções no menu de contexto para citar um link ou um DOI/ISBN selecionado sem abri-lo. Ela não executa nenhum código nas páginas que você visita e precisa apenas das permissões activeTab e contextMenus. Baixe-a nesta página e carregue-a em chrome://extensions; ela funciona no Chrome, Edge, Brave e outros navegadores baseados em Chromium.",
    faqAddQ: "Como adiciono o bookmarklet?",
    faqAddA:
      "Certifique-se de que sua barra de favoritos esteja visível e arraste o botão 'Cite esta página' desta página até ela. Se não for possível arrastar (por exemplo, no celular), crie um favorito manualmente e cole o código do bookmarklet como a URL.",
    faqSafeQ: "É seguro? Ele lê a página em que estou?",
    faqSafeA:
      "Sim, é seguro. O bookmarklet não executa nenhum código na página que você está lendo — ele apenas envia o endereço web dessa página ao CitationEasy, onde nosso servidor busca e analisa os metadados públicos da página, exatamente como faz quando você cola uma URL manualmente.",
    faqOtherSourcesQ: "Funciona para fontes além de sites?",
    faqOtherSourcesA:
      "O bookmarklet é para páginas da web, mas o link subjacente também aceita um DOI, ISBN, PMID ou ID do arXiv por meio do parâmetro ?source= — então um link compartilhável de 'citar isto' também funciona para artigos de periódico e livros.",
    relatedHeading: "Ferramentas relacionadas",
    relatedGenerator: "Gerador de citações",
    relatedHowToCite: "Como citar um site",
    relatedTools: "Todas as ferramentas",
  },

  // /shared-bibliography — opens a bibliography that travelled inside a share link.
  sharedBibliographyPage: {
    metaTitle:
      "Bibliografia compartilhada - Ver e importar citações compartilhadas",
    metaDescription:
      "Abra uma bibliografia que alguém compartilhou com você a partir do CitationEasy. Veja as citações formatadas, copie-as ou importe a lista inteira para sua própria bibliografia — grátis, sem conta, e os dados ficam no próprio link.",
    ogTitle: "Bibliografia compartilhada",
    ogDescription:
      "Veja e importe uma bibliografia compartilhada. As citações viajam dentro do link — nada é armazenado em um servidor, e os links nunca expiram.",
    breadcrumbRoot: "CitationEasy",
    breadcrumbLabel: "Trilha de navegação",
    breadcrumbHome: "Início",
    breadcrumbCurrent: "Bibliografia compartilhada",
    heading: "Bibliografia compartilhada",
    intro:
      "Alguém compartilhou estas citações com você. Copie-as ou importe a lista inteira para sua própria bibliografia do CitationEasy com um clique.",
    loading: "Abrindo a bibliografia compartilhada…",
    copyAll: "Copiar tudo",
    copied: "Copiado",
    copyFailed: "Falha ao copiar",
    citationOne: "citação",
    citationMany: "citações",
    addAll: "Adicionar tudo à minha bibliografia",
    added: "{count} adicionadas à sua bibliografia",
    skippedOne: "{count} duplicata ignorada",
    skippedMany: "{count} duplicatas ignoradas",
    openGenerator: "abrir o gerador",
    emptyHeading: "Nenhuma bibliografia compartilhada neste link",
    emptyBody:
      "Esta página exibe bibliografias compartilhadas a partir do CitationEasy. Para compartilhar a sua, abra sua bibliografia no [gerador de citações](/), escolha **Exportar → Copiar link de compartilhamento** e envie o link para quem quiser. As citações viajam dentro do próprio link — nada é armazenado em um servidor, então os links continuam funcionando para sempre e seus dados permanecem privados.",
    decodeError: "Não foi possível abrir este link compartilhado.",
  },

  // The 19 source types the 15 detailed styles show worked examples for
  // (`DetailedStyleData.examples[].sourceType`). 75 rows, 19 distinct values — so this
  // is a vocabulary, not per-page copy, and it belongs here rather than repeated in
  // every locale's `src/content/style-pages/<locale>.json`.
  //
  // Two casings for the same reason `howToCitePage.refList*Lower` has two: the pages
  // print the label as a heading ("Book") and inside a sentence ("a sample APA entry
  // for a book"). Lower-casing the translated noun in code is wrong in German, where
  // every one of these is capitalised, so the lower form is a real key a translator
  // fills rather than a `.toLowerCase()` call.
  //
  // The citations themselves — `referenceEntry` and `inTextCitation` — are NOT here and
  // are not translatable anywhere: they are worked examples in a specific style's own
  // punctuation. See `src/lib/localized-content.ts`.
  sourceTypes: {
    book: "Livro",
    bookLower: "livro",
    bookChapter: "Capítulo de livro",
    bookChapterLower: "capítulo de livro",
    case: "Processo",
    caseLower: "processo",
    conferencePaper: "Artigo de congresso",
    conferencePaperLower: "artigo de congresso",
    courtCase: "Caso judicial",
    courtCaseLower: "caso judicial",
    federalStatute: "Lei federal",
    federalStatuteLower: "lei federal",
    governmentDocument: "Documento governamental",
    governmentDocumentLower: "documento governamental",
    governmentReport: "Relatório governamental",
    governmentReportLower: "relatório governamental",
    journalArticle: "Artigo de periódico",
    journalArticleLower: "artigo de periódico",
    lawReviewArticle: "Artigo de revista jurídica",
    lawReviewArticleLower: "artigo de revista jurídica",
    legislation: "Legislação",
    legislationLower: "legislação",
    newspaperArticle: "Artigo de jornal",
    newspaperArticleLower: "artigo de jornal",
    patent: "Patente",
    patentLower: "patente",
    podcast: "Podcast",
    podcastLower: "podcast",
    preprint: "Preprint",
    preprintLower: "preprint",
    standard: "Norma",
    standardLower: "norma",
    thesis: "Tese",
    thesisLower: "tese",
    website: "Site",
    websiteLower: "site",
    youtubeVideo: "Vídeo do YouTube",
    youtubeVideoLower: "vídeo do YouTube",
  },

  // Chrome shared by the five per-style guide routes — /examples, /format,
  // /in-text-citation, /reference-page and /annotated-bibliography, 15 styles each.
  //
  // One section rather than five copies: the CTA, the back link and the cross-links
  // between the five pages are the same sentences on every one of them, and duplicating
  // them per route would be five identical translation units instead of one.
  styleGuidePages: {
    backToStyle: "Voltar a {style}",
    backToGenerator: "Voltar ao gerador de citações",
    ctaHeading: "Gere citações em {style} automaticamente",
    ctaButton: "Citar em {style} agora →",
    faqHeading: "Perguntas frequentes",
    resourcesHeading: "Mais recursos",
    relatedHeading: "Guias de {style} relacionados",
    resourceGenerator: "Gerador de citações gratuito ({count} estilos)",
    resourceTools: "Ferramentas de escrita gratuitas",
    resourceGuides: "Todos os guias de citação",
    resourceStyleGenerator: "Gerador de citações em {style}",
    resourceExamples: "Exemplos de citação em {style}",
    resourceFormat: "Guia de formatação {style}",
    resourceInText: "Guia de citação no texto em {style}",
    resourceAnnotatedBib: "Guia de bibliografia anotada em {style}",
    resourceReferencePage: "Guia de {list} em {style}",
    notFoundTitle: "Não encontrado",
  },

  // /examples/<style> — the copy-ready worked examples for all 15 detailed styles.
  examplesPage: {
    metaTitle:
      "Exemplos de citação em {style} - Todos os tipos de fonte [2026]",
    metaDescription:
      "Veja exemplos de citação em {style} ({edition}) formatados corretamente para livros, artigos de periódico, sites e mais. Entradas de {list} prontas para copiar e citações no texto.",
    ogTitle: "Exemplos de citação em {style}",
    ogDescription:
      "Entradas em {style} prontas para copiar para cada tipo de fonte comum.",
    ogImageTitle: "Exemplos de citação em {style}",
    ogKind: "{style} {edition}",
    keywordExamples: "exemplos de citação em {style}",
    keywordReference: "exemplos de referência em {style}",
    keywordInText: "exemplos de citação no texto em {style}",
    keywordList: "exemplos de {listLower} em {style}",
    keywordHowTo: "como citar em {style}",
    breadcrumb: "Exemplos de citação em {style}",
    heading: "Exemplos de citação em {style}",
    lead: "Veja exemplos de citação em {style} ({edition}) formatados corretamente para {count} tipos de fonte comuns. Cada exemplo traz a entrada de {listLower} e a citação no texto correspondente.",
    entryLabel: "Entrada de {list}:",
    inTextLabel: "Citação no texto:",
    ctaBody:
      "Pule a formatação manual. Cole um DOI, uma URL ou um ISBN no CitationEasy e receba uma citação em {style} perfeitamente formatada em segundos.",
    faqLookQ: "Como é uma citação em {style}?",
    faqLookA:
      "Uma citação em {style} inclui uma entrada de {listLower} com todos os detalhes da fonte e uma citação no texto. O formato varia conforme o tipo de fonte. Por exemplo, uma citação de livro em {style} ({edition}) inclui o autor, o ano, o título e a editora.",
    faqHowManyQ: "Quantos exemplos de citação em {style} estão disponíveis?",
    faqHowManyA:
      "O CitationEasy oferece exemplos de citação em {style} para {count} tipos de fonte, incluindo {types}. Cada exemplo traz a entrada de {listLower} e a citação no texto correspondente.",
    faqCopyQ: "Posso copiar estes exemplos de citação em {style}?",
    faqCopyA:
      "Sim, os exemplos em {style} nesta página mostram a formatação exata de cada tipo de fonte. Você pode usá-los como modelos para suas próprias citações. Para formatação automática, use nosso gerador de citações gratuito em {style}.",
  },

  // /format/<style> — font, margins, spacing, title page and headers. The VALUES those
  // labels take are per-style prose and live in the overlay, not here.
  formatPage: {
    metaTitle:
      "Guia de formatação {style} - Regras de formatação do trabalho [2026]",
    metaDescription:
      "Guia completo de formatação de trabalhos em {style} ({edition}). Conheça as regras corretas de fonte, margens, espaçamento, folha de rosto, cabeçalhos e formatação de {listLower}.",
    ogTitle: "Formatação de trabalho em {style}",
    ogDescription:
      "Margens, fonte, espaçamento e folha de rosto de um trabalho em {style}.",
    ogImageTitle: "Formatação de trabalho em {style}",
    ogImageSubtitle: "Margens de {margins} · {spacing}",
    ogKind: "{style} {edition}",
    keywordFormat: "formato {style}",
    keywordPaperFormat: "formatação de trabalho em {style}",
    keywordRules: "regras de formatação {style}",
    keywordMargins: "margens fonte espaçamento {style}",
    keywordList: "formato de {listLower} em {style}",
    breadcrumb: "Guia de formatação {style}",
    heading: "Guia de formatação {style}",
    lead: "Siga estas regras de formatação de trabalho em {style} ({edition}) para garantir que seu documento atenda aos padrões exigidos. Este guia cobre fonte, margens, espaçamento, folha de rosto, cabeçalhos e formatação de {listLower}.",
    paperHeading: "Formatação do trabalho",
    paperIntro:
      "Veja abaixo os principais requisitos de formatação para trabalhos escritos no estilo {style} ({edition}).",
    labelFont: "Fonte",
    labelFontSize: "Tamanho da fonte",
    labelMargins: "Margens",
    labelSpacing: "Espaçamento",
    labelTitlePage: "Folha de rosto",
    labelHeaders: "Cabeçalhos/Números de página",
    listHeading: "Formatação de {list}",
    listIntro:
      "A parte {listLower} deve seguir estas regras para ter a formatação correta no estilo {style}:",
    ctaBody:
      "Deixe o CitationEasy cuidar da formatação. Cole um DOI, uma URL ou um ISBN e receba uma citação em {style} perfeitamente formatada em segundos.",
    faqFontQ: "Que fonte e tamanho devo usar no formato {style}?",
    faqFontA:
      "{style} ({edition}) exige {font}. O tamanho de fonte padrão é {fontSize}.",
    faqMarginsQ: "Quais são os requisitos de margem para {style}?",
    faqMarginsA:
      "Os trabalhos em {style} exigem {margins}. Isso vale para as margens superior, inferior, esquerda e direita de cada página.",
    faqTitlePageQ: "{style} exige folha de rosto?",
  },

  // /in-text-citation/<style> — the six in-text forms. The forms themselves
  // ("(Smith, 2024)") are per-style prose in the overlay.
  inTextCitationPage: {
    metaTitle: "Guia de citação no texto em {style} - Regras e exemplos [2026]",
    metaDescription:
      "Aprenda a formatar citações no texto em {style} ({edition}). Regras para um autor, vários autores, citações diretas, sem autor e sem data, com exemplos.",
    ogTitle: "Citações no texto em {style}",
    ogDescription:
      "Todas as formas de citação no texto em {style}, com exemplos.",
    ogImageTitle: "Citações no texto em {style}",
    ogImageSubtitle: "Um autor {singleAuthor} · três ou mais {threeOrMore}",
    ogKind: "{style} {edition}",
    keywordInText: "citação no texto em {style}",
    keywordExamples: "exemplos de citação no texto em {style}",
    keywordParenthetical: "citação entre parênteses em {style}",
    keywordRules: "regras de citação {style}",
    keywordHowTo: "como citar no texto em {style}",
    breadcrumb: "Citações no texto em {style}",
    heading: "Citações no texto em {style}",
    lead: "Domine as citações no texto em {style} ({edition}) com este guia completo. Aprenda o formato correto para cada situação, incluindo um e vários autores, citações diretas e fontes com informações incompletas.",
    rulesHeading: "Regras de citação no texto",
    rulesIntro:
      "Veja abaixo as regras de citação no texto em {style} para cada situação comum que você encontrará ao escrever seu trabalho.",
    labelSingleAuthor: "Um autor",
    labelTwoAuthors: "Dois autores",
    labelThreeOrMore: "Três ou mais autores",
    labelDirectQuote: "Citação direta",
    labelNoAuthor: "Sem autor",
    labelNoDate: "Sem data",
    examplesHeading: "Exemplos rápidos",
    examplesIntro:
      "Aqui estão exemplos reais de citação no texto em {style} para diferentes tipos de fonte:",
    ctaBody:
      "Pare de se preocupar com a formatação da citação no texto. Cole um DOI, uma URL ou um ISBN no CitationEasy e receba na hora tanto a entrada de {listLower} quanto a citação no texto.",
    faqFormatQ: "Como formatar uma citação no texto em {style}?",
    faqFormatA:
      "Para um único autor em {style}, use o formato {singleAuthor}. Para dois autores, use {twoAuthors}. Para três ou mais autores, use {threeOrMore}.",
    faqQuoteQ: "Como citar uma citação direta em {style}?",
    faqQuoteA:
      "Para citações diretas em {style} ({edition}), use o formato: {directQuote}.",
    faqMissingQ: "E se não houver autor ou data em {style}?",
    faqMissingA:
      "Quando não há autor em {style}, use: {noAuthor}. Quando não há data, use: {noDate}.",
  },

  // /reference-page/<style> — how to build the list at the back of the paper.
  //
  // The five `mistake*` strings carry `**bold**` inline markup because the lead-in of
  // each bullet is emphasised inside the sentence; they render through `InlineText`.
  referenceListPage: {
    metaTitle: "Guia de {list} em {style} - Regras de formatação [2026]",
    metaDescription:
      "Aprenda a formatar a parte {listLower} em {style} ({edition}). Regras de formatação passo a passo, exemplos e erros comuns a evitar em {fullName}.",
    ogTitle: "{listLower} em {style}",
    ogDescription: "Como montar a parte {list} em {style}.",
    ogImageTitle: "{list} em {style}",
    ogKind: "{style} {edition}",
    breadcrumb: "Como formatar a parte {list} em {style}",
    heading: "Como formatar a parte {list} em {style}",
    lead: "A parte {listLower} é um dos elementos mais importantes de qualquer trabalho acadêmico formatado em {style} ({edition}). Ela fornece aos leitores os detalhes completos de cada fonte citada, permitindo que localizem e verifiquem suas referências. Acertar a formatação é essencial para a credibilidade acadêmica. Este guia cobre as regras exatas, mostra exemplos reais e destaca os erros mais comuns cometidos pelos estudantes.",
    rulesHeading: "Regras de formatação",
    rulesIntro: "Siga estas regras ao criar a parte {listLower} em {style}:",
    exampleHeading: "Exemplo de {list}",
    exampleIntro:
      "Veja como fica a parte {listLower} em {style} com entradas para diferentes tipos de fonte:",
    mistakesHeading: "Erros comuns a evitar",
    mistakesIntro:
      "Estes são os cinco erros de formatação mais frequentes cometidos pelos estudantes ao criar a parte {listLower} em {style}:",
    mistakeIndent:
      "**Recuo deslocado incorreto ou ausente:** cada entrada deve ter a primeira linha alinhada à esquerda e as linhas seguintes com recuo de 1,27 cm (0,5 polegada). Use as configurações de parágrafo do seu editor de texto em vez de pressionar a tecla Tab.",
    mistakeOrder:
      '**Ordem alfabética incorreta:** as entradas devem ser organizadas de acordo com as regras de {style}. Ignore artigos como "A," "An" e "The" (ou "o," "a," "os," "as") ao alfabetizar. Nos estilos numéricos, liste as entradas na ordem em que aparecem pela primeira vez no texto.',
    mistakeSpacing:
      "**Espaçamento inconsistente:** revise o espaçamento entre linhas em toda a parte {listLower}. Misturar espaçamento simples e duplo é um erro comum e fácil de não perceber.",
    mistakeItalics:
      "**Uso incorreto do itálico:** use itálico nos títulos de obras independentes, como livros e periódicos, mas não em títulos de artigos ou capítulos. Consulte as diretrizes de {style} para saber exatamente quais elementos devem ficar em itálico.",
    mistakeLinks:
      "**DOIs e URLs ausentes ou quebrados:** inclua sempre um DOI quando disponível, formatado como um link clicável. Teste cada URL antes de entregar seu trabalho para garantir que leva à fonte correta.",
    ctaHeading: "Gere a parte {list} automaticamente",
    ctaBody:
      "Pule a formatação manual. Cole um DOI, uma URL ou um ISBN no CitationEasy e receba uma citação em {style} perfeitamente formatada em segundos, pronta para a parte {listLower}.",
    faqWhatQ: "O que é a parte {list} em {style}?",
    faqWhatA:
      "A parte {list} em {style} é o trecho final do seu trabalho que lista todas as fontes citadas no texto, formatada de acordo com as diretrizes de {fullName} ({edition}). Ela permite que os leitores localizem e verifiquem cada fonte referenciada.",
    faqFormatQ: "Como formatar a parte {listLower} em {style}?",
    faqFormatA:
      'Comece em uma nova página, dê a ela o título "{list}" e liste todas as fontes citadas no seu trabalho. Siga as regras específicas de {style} para recuo deslocado, espaçamento, ordem alfabética ou numérica, e itálico.',
    faqDifferenceQ:
      "Qual é a diferença entre a parte {listLower} e uma bibliografia?",
    faqDifferenceA:
      'A parte {listLower} inclui apenas as fontes citadas diretamente no seu trabalho, enquanto uma bibliografia também pode incluir fontes consultadas mas não citadas. Em {style} ({edition}), a lista no final do trabalho é chamada de "{list}."',
  },

  // /annotated-bibliography/<style>. `whatTypes` carries `**bold**` inline markup.
  //
  // `exampleAnnotation` is a specimen annotation, not a citation: it is ordinary prose
  // about an imaginary source and translates like any other paragraph. The reference
  // entry printed above it does not — that comes from `DETAILED_STYLES` and stays as
  // the style wrote it.
  annotatedBibPage: {
    metaTitle: "Guia e exemplos de bibliografia anotada em {style} [2026]",
    metaDescription:
      "Aprenda a escrever uma bibliografia anotada em {style} ({edition}). Inclui regras de formatação, exemplos de entradas e dicas para bibliografias anotadas em {fullName}.",
    ogTitle: "Bibliografia anotada em {style}",
    ogDescription: "Como formatar uma bibliografia anotada em {style}.",
    ogImageTitle: "Bibliografia anotada em {style}",
    ogKind: "{style} {edition}",
    breadcrumb: "Bibliografia anotada em {style}",
    heading: "Bibliografia anotada em {style}",
    lead: "Uma bibliografia anotada é uma lista de citações de fontes em que cada uma inclui um breve parágrafo descritivo ou avaliativo. No formato {style} ({edition}), cada entrada começa com uma referência corretamente formatada, seguida de uma anotação que resume, avalia ou reflete sobre a fonte. Este guia apresenta as regras, a formatação e os exemplos necessários para criar uma bibliografia anotada em {style} bem elaborada.",
    whatHeading: "O que é uma bibliografia anotada?",
    whatBody:
      "Uma bibliografia anotada vai além da parte {listLower} padrão ao acrescentar um breve parágrafo após cada citação. Essa anotação dá aos leitores uma noção do conteúdo, da qualidade e da relevância da fonte. Os professores costumam pedir bibliografias anotadas para ajudar os estudantes a avaliar suas fontes de forma crítica e mostrar como cada uma contribui para o tema de pesquisa.",
    whatTypes:
      "Existem três tipos comuns de anotações: as **descritivas**, que resumem a fonte; as **avaliativas**, que avaliam a qualidade e a confiabilidade do trabalho; e as **combinadas**, que fazem as duas coisas. As instruções da sua tarefa devem especificar qual tipo usar.",
    rulesHeading: "Regras de bibliografia anotada em {style}",
    rulesIntro:
      "Siga estas regras ao formatar uma bibliografia anotada em {fullName}:",
    exampleHeading: "Exemplo de entrada de bibliografia anotada",
    exampleIntro:
      "Veja abaixo um exemplo de entrada de bibliografia anotada em {style} para um(a) {sourceType}:",
    exampleAnnotation:
      "Esta fonte oferece uma visão geral abrangente do tema, apresentando argumentos bem fundamentados e respaldados por evidências empíricas. O autor recorre a uma ampla gama de fontes primárias e secundárias para construir um caso persuasivo. A metodologia é rigorosa e os resultados são apresentados com clareza, o que torna este trabalho um recurso valioso para compreender o assunto. Esta fonte é especialmente útil para estabelecer o referencial teórico do projeto de pesquisa e oferece perspectivas que complementam outros trabalhos da bibliografia.",
    tipsHeading: "Dicas para escrever anotações em {style}",
    tipSummarize:
      "Comece resumindo a fonte em duas ou três frases e, em seguida, acrescente sua avaliação dos pontos fortes, das limitações e da relevância.",
    tipTone:
      "Use a terceira pessoa e um tom acadêmico em toda a anotação. Evite linguagem informal ou reflexões em primeira pessoa, a menos que seu professor peça o contrário.",
    tipLength:
      "Mantenha cada anotação entre 100 e 200 palavras. Seja conciso e concentre-se nos aspectos mais importantes da fonte.",
    tipRelevance:
      "Explique como a fonte se encaixa na sua pesquisa geral. Descreva que perspectiva ou evidência única ela traz.",
    tipCitation:
      "Certifique-se de que a parte da citação de cada entrada siga exatamente as regras de formatação de {fullName} antes de escrever a anotação.",
    ctaHeading: "Monte sua bibliografia anotada automaticamente",
    ctaBody:
      "Use o criador de bibliografia anotada do CitationEasy para gerar citações em {style} corretamente formatadas e depois adicione suas anotações.",
    ctaButton: "Abrir o criador de bibliografia anotada →",
    faqWhatQ: "O que é uma bibliografia anotada em {style}?",
    faqWhatA:
      "Uma bibliografia anotada em {style} ({edition}) é uma lista de citações formatadas no estilo {fullName}, cada uma seguida de um breve parágrafo que resume, avalia ou reflete sobre a fonte. Ela ajuda os leitores a entender a relevância e a qualidade de cada fonte.",
    faqLengthQ: "Qual deve ser o tamanho das anotações?",
    faqLengthA:
      "As anotações costumam ter entre 100 e 200 palavras. Algumas tarefas podem exigir anotações mais curtas ou mais longas, dependendo da profundidade de análise esperada. Consulte sempre as orientações do seu professor para requisitos específicos de tamanho.",
    faqIncludeQ: "O que uma anotação deve incluir?",
    faqIncludeA:
      "Uma anotação deve incluir um breve resumo do argumento ou dos principais achados da fonte, uma avaliação da credibilidade e utilidade da fonte, e uma reflexão sobre como ela se encaixa na sua pesquisa. Algumas anotações são puramente descritivas, enquanto outras incluem análise crítica.",
  },

  guidePage: {
    byline: "Por {author}",
    published: "Publicado em {date}",
    updated: "Atualizado em {date}",
    readingTime:
      "one:{count} minuto de leitura|other:{count} minutos de leitura",
    wordCount: "one:{count} palavra|other:{count} palavras",
  },

  // /guides hub, /guides/<category> index pages, and the chrome around each guide
  guideLibrary: {
    eyebrow: "Biblioteca de guias",
    browseByCategory: "Explorar por categoria",
    recentlyUpdated: "Atualizados recentemente",
    allGuides: "Todos os guias",
    guideCount: "one:{count} guia|other:{count} guias",
    updatedOn: "Atualizado em {date}",
    minRead: "one:{count} min de leitura|other:{count} min de leitura",
    onThisPage: "Nesta página",
    viewAllInCategory: "Todos os guias de {category}",
    moreInCategory: "Mais em {category}",
    aboutThisGuide: "Sobre este guia",
    provenance:
      "Escrito por {author}. Publicado em {published}; última atualização em {updated}.",
    categoryIntroSuffix:
      "Todos os guias são gratuitos, escritos para estudantes e pesquisadores e datados para que você veja quando foram revisados pela última vez.",
    photoCredit: "Foto: {author}, {source} ({license})",
    imageAlt: "Fotografia que ilustra os guias de {category}",
    imageSourceCommons: "Wikimedia Commons",
    apaStyleName: "Estilo APA",
    apaStyleDescription:
      "Como citar sites, artigos de periódicos, livros, vídeos e mais na APA 7.ª edição, com exemplos de lista de referências e citações no texto.",
    mlaStyleName: "Estilo MLA",
    mlaStyleDescription:
      "Como citar todas as fontes comuns na MLA 9.ª edição, de páginas da web e artigos a poemas, peças, filmes e materiais de curso.",
    chicagoStyleName: "Estilo Chicago",
    chicagoStyleDescription:
      "Guias do Chicago Manual of Style com citações em notas-bibliografia e autor-data para livros, artigos, arquivos, documentos governamentais e mídia.",
    styleComparisonsName: "Comparações de estilos",
    styleComparisonsDescription:
      "Comparações lado a lado de APA, MLA, Chicago, Harvard, Vancouver, IEEE e mais, com orientações sobre qual estilo sua área exige.",
    researchMethodsName: "Métodos de pesquisa",
    researchMethodsDescription:
      "Como planejar um trabalho de pesquisa, revisar a literatura, fazer anotações, avaliar fontes, evitar plágio e montar uma bibliografia.",
    academicWritingName: "Escrita acadêmica",
    academicWritingDescription:
      "Guias de redação para resumos, introduções, conclusões, teses, seções de metodologia e prosa acadêmica clara.",
    fieldSpecificName: "Por área",
    fieldSpecificDescription:
      "Guias de citação por disciplina: psicologia, história, literatura, medicina, engenharia, direito, enfermagem e mais.",
    sourceEvaluationName: "Avaliação de fontes",
    sourceEvaluationDescription:
      "Como julgar credibilidade, distinguir fontes acadêmicas de populares, usar o Google Acadêmico e bases de dados e identificar vieses.",
    citationHelpName: "Ajuda com citações",
    citationHelpDescription:
      "Respostas para os casos difíceis: sem autor ou data, vários autores, PDFs, ferramentas de IA, casos jurídicos e importação ou exportação de citações.",
  },

  common: {
    backToGuides: "← Voltar aos guias",
    free: "Gratuito",
    noSignup: "Sem cadastro",
    backToHome: "Voltar ao gerador de referências",
    inEnglish: "Em inglês",
    viewInEnglish: "Ver em inglês",
    availableInEnglish:
      "Esta página está disponível em inglês com mais detalhes.",
    otherLanguages: "Outros idiomas",
    skipToContent: "Ir para o conteúdo",
    cookieNotice:
      "Usamos cookies para análise e publicidade para manter o CitationEasy gratuito.",
    privacyPolicy: "Política de privacidade",
    accept: "Aceitar",
    decline: "Recusar",
    advertisement: "Publicidade",
  },
  legalPage: {
    backLink: "← Voltar ao gerador",
    effective: "Data de entrada em vigor:",
    updated: "Última atualização:",
  },
};

export default pt;
